import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PhotoAssetBrowser from './PhotoAssetBrowser';
import SelectedAssetPreview from './SelectedAssetPreview';
import { isImageAsset, resolvePreviewKind } from './assetUtils';
import { reconcileCampaignHeroAssets } from './campaignAssetSelection';
import TVLayoutEditor from './TVLayoutEditor';
import { getSubscriptionTypeFromToken } from '../../utils/jwt';
import {
  TV_TEMPLATES,
  ensureLayouts,
  ensureTvLayout,
  deepClone,
  getTemplateById,
} from './tvTemplates';

const LANGUAGE_LABELS = {
  'en-AU': 'English - Australia',
  'en-GB': 'English - Great Britain',
  'en-US': 'English - United States',
  'en-CA': 'English - Canada',
  'en-IN': 'English - India',
  'vi-VN': 'Vietnamese - Vietnam',
};

const CONTENT_TRANSLATION_LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'vi', label: 'Vietnamese' },
  { code: 'ko', label: 'Korean' },
  { code: 'ru', label: 'Russian' },
  { code: 'zh', label: 'Chinese (Simplified)' },
  { code: 'zh-TW', label: 'Chinese (Traditional)' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ja', label: 'Japanese' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'es', label: 'Spanish' },
  { code: 'th', label: 'Thai' },
  { code: 'id', label: 'Indonesian' },
  { code: 'ms', label: 'Malay' },
];

const TV_OPTIONS = [
  { key: 'tv_hd', label: 'TV — 1920×1080', mode: 'custom', w: 1920, h: 1080 }
];
const FB_OPTIONS = [
  { key: 'fb_landscape', label: 'Facebook — 1200×630', mode: 'custom', w: 1200, h: 630 },
  { key: 'fb_square', label: 'Facebook — 1080×1080', mode: 'custom', w: 1080, h: 1080 },
  { key: 'fb_portrait', label: 'Facebook — 1080×1350', mode: 'custom', w: 1080, h: 1350 }
];
const GMB_OPTIONS = [
  { key: 'google_post', label: 'Google Business — 1200×900', mode: 'custom', w: 1200, h: 900 }
];

const MONETISATION_MODE_OPTIONS = [
  { value: 'off', label: 'Off', description: 'No automatic landing page monetisation.' },
  { value: 'subtle', label: 'Subtle', description: 'Light inline monetisation only.' },
  { value: 'standard', label: 'Standard', description: 'Inline links plus activity blocks.' },
  { value: 'full', label: 'Full', description: 'Apply the complete monetisation mix.' },
];
const DEFAULT_VOUCHER_REDEMPTION_METHOD = 'qr_confirm_page';
const HOURS_PER_DAY = 24;
const DAYS_PER_MONTH = 30;
const HOURS_PER_MONTH = DAYS_PER_MONTH * HOURS_PER_DAY;
const DEFAULT_VOUCHER_EXPIRY_HOURS = 7 * HOURS_PER_DAY;
const DEFAULT_VOUCHER_START_DELAY_HOURS = 8;
const MAX_VOUCHER_EXPIRY_HOURS = 365 * HOURS_PER_DAY;
const DEFAULT_VOUCHER_TERMS_FONT_SIZE = 16;
const DEFAULT_HERO_SLIDESHOW_INTERVAL_SECONDS = 5;
const DEFAULT_CAMPAIGN_THEME_DISPLAY = {
  pageBackgroundColor: '#0f172a',
  pageBackgroundAccentColor: '#334155',
  panelBackgroundColor: '#0f172a',
  sectionBackgroundColor: '#1e293b',
  headingTextColor: '#f8fafc',
  bodyTextColor: '#e2e8f0',
  accentColor: '#38bdf8',
  mutedTextColor: '#94a3b8',
};
const DEFAULT_VOUCHER_BACKGROUND_DISPLAY_COLOR = '#10b981';
const DEFAULT_VOUCHER_TEXT_DISPLAY_COLOR = '#052e2b';
const PANEL_TITLES = {
  image: 'Artwork Chooser',
  tv: 'TV Content',
  social: 'Social Posts',
  announcement: 'Announcements',
  overlay: 'Overlay Ads',
  landing: 'Landing Page',
  advertising: 'Advertising',
  translations: 'Translations',
};

const normalizeLanguageCode = (value, fallback = '') => {
  const normalized = String(value || '').trim();
  return normalized || fallback;
};

const getLanguageKey = (value) => normalizeLanguageCode(value).toLowerCase();

const sameLanguageCode = (a, b) => {
  const left = getLanguageKey(a);
  const right = getLanguageKey(b);
  return Boolean(left) && left === right;
};

const getLanguageLabel = (code) => {
  const normalizedCode = normalizeLanguageCode(code);
  return CONTENT_TRANSLATION_LANGUAGE_OPTIONS.find((option) => option.code === normalizedCode)?.label
    || LANGUAGE_LABELS[normalizedCode]
    || normalizedCode
    || 'Language';
};

const formatTimestamp = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const getTranslationFreshnessClasses = (freshness) => {
  if (freshness === 'stale') return 'border-amber-700/60 bg-amber-950/40 text-amber-100';
  if (freshness === 'source') return 'border-sky-700/60 bg-sky-950/40 text-sky-100';
  return 'border-emerald-700/60 bg-emerald-950/40 text-emerald-100';
};

const getTranslationReviewClasses = (reviewStatus) => {
  if (reviewStatus === 'approved') return 'border-emerald-700/60 bg-emerald-950/40 text-emerald-100';
  if (reviewStatus === 'reviewed') return 'border-blue-700/60 bg-blue-950/40 text-blue-100';
  return 'border-gray-700 bg-gray-950/40 text-gray-200';
};

const normalizeMonetisationMode = (value, fallback = 'off') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (MONETISATION_MODE_OPTIONS.some((option) => option.value === normalized)) return normalized;
  return fallback;
};

const normalizePositiveInteger = (value, fallback, { min = 1, max = Number.MAX_SAFE_INTEGER } = {}) => {
  const normalized = Number(value);
  if (!Number.isFinite(normalized)) return fallback;
  const rounded = Math.trunc(normalized);
  if (rounded < min) return min;
  if (rounded > max) return max;
  return rounded;
};

const normalizeHexColor = (value, fallback = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return fallback;
  return /^#[0-9a-f]{6}$/i.test(normalized) ? normalized : fallback;
};

const resolveEditorColor = (value, fallback) => normalizeHexColor(value, fallback);

const buildDefaultVoucherOffer = () => ({
  enabled: false,
  headline: '',
  body: '',
  submitLabel: 'Get voucher on this phone',
  offerTitle: '',
  terms: '',
  termsFontSize: DEFAULT_VOUCHER_TERMS_FONT_SIZE,
  termsStyle: 'normal',
  startsAfterEnabled: false,
  startsAfterHours: DEFAULT_VOUCHER_START_DELAY_HOURS,
  expiresAfterHours: DEFAULT_VOUCHER_EXPIRY_HOURS,
  maxClaimsPerDevicePerCampaign: 1,
  redemptionMethod: DEFAULT_VOUCHER_REDEMPTION_METHOD,
  backgroundColor: '',
  textColor: '',
  landingBackgroundColor: '',
  landingTextColor: '',
});

const buildDefaultCampaignLandingTheme = () => ({
  pageBackgroundColor: '',
  pageBackgroundAccentColor: '',
  panelBackgroundColor: '',
  sectionBackgroundColor: '',
  headingTextColor: '',
  bodyTextColor: '',
  accentColor: '',
  mutedTextColor: '',
});

const buildDefaultHeroSlideshow = () => ({
  enabled: false,
  autoRotate: true,
  intervalSeconds: DEFAULT_HERO_SLIDESHOW_INTERVAL_SECONDS,
});

const normalizeCampaignHeroAssetIds = (value, fallback = []) => {
  const source = Array.isArray(value)
    ? value
    : (Array.isArray(fallback) ? fallback : [fallback]);
  return Array.from(new Set(
    source
      .map((entry) => Number(entry))
      .filter((entry) => Number.isFinite(entry) && entry > 0)
      .map((entry) => Math.trunc(entry))
  ));
};

const normalizeHeroSlideshow = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const defaults = buildDefaultHeroSlideshow();
  return {
    ...defaults,
    enabled: Boolean(source.enabled),
    autoRotate: source.autoRotate === undefined ? defaults.autoRotate : Boolean(source.autoRotate),
    intervalSeconds: normalizePositiveInteger(
      source.intervalSeconds ?? source.interval_seconds,
      defaults.intervalSeconds,
      { min: 2, max: 30 }
    ),
  };
};

const normalizeSocialSource = (value) => {
  if (!value || typeof value !== 'object' || value.provider !== 'facebook') return null;
  return {
    provider: 'facebook',
    postId: Number(value.postId || 0) || null,
    permalink: typeof value.permalink === 'string' ? value.permalink : '',
    qrDestination: value.qrDestination === 'landing_page' ? 'landing_page' : 'original_post',
  };
};

const normalizeFacebookPublication = (value) => {
  if (!value || typeof value !== 'object' || !value.externalPostId) return null;
  return {
    accountId: Number(value.accountId || 0) || null,
    accountName: typeof value.accountName === 'string' ? value.accountName : '',
    externalPostId: String(value.externalPostId),
    mediaCount: Math.max(0, Number(value.mediaCount || 0) || 0),
    publishedAt: typeof value.publishedAt === 'string' ? value.publishedAt : '',
    permalink: typeof value.permalink === 'string' ? value.permalink : '',
  };
};

const normalizeVoucherOffer = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const defaults = buildDefaultVoucherOffer();
  const normalizedTermsStyle = (() => {
    const candidate = String(source.termsStyle ?? source.terms_style ?? '').trim().toLowerCase();
    return ['normal', 'italic', 'bold'].includes(candidate) ? candidate : defaults.termsStyle;
  })();
  const normalizedEnabled = typeof source.enabled === 'string'
    ? ['1', 'true', 'yes', 'on'].includes(source.enabled.trim().toLowerCase())
    : Boolean(source.enabled);
  return {
    ...defaults,
    enabled: normalizedEnabled,
    headline: typeof source.headline === 'string' ? source.headline : '',
    body: typeof source.body === 'string' ? source.body : '',
    submitLabel: typeof source.submitLabel === 'string' && source.submitLabel.trim()
      ? source.submitLabel
      : defaults.submitLabel,
    offerTitle: typeof source.offerTitle === 'string' ? source.offerTitle : '',
    terms: typeof source.terms === 'string' ? source.terms : '',
    termsFontSize: normalizePositiveInteger(
      source.termsFontSize ?? source.terms_font_size,
      defaults.termsFontSize,
      { min: 12, max: 24 }
    ),
    termsStyle: normalizedTermsStyle,
    startsAfterEnabled: source.startsAfterEnabled === undefined
      ? defaults.startsAfterEnabled
      : Boolean(source.startsAfterEnabled),
    startsAfterHours: normalizePositiveInteger(
      source.startsAfterHours,
      defaults.startsAfterHours,
      { min: 1, max: MAX_VOUCHER_EXPIRY_HOURS }
    ),
    expiresAfterHours: normalizePositiveInteger(source.expiresAfterHours, defaults.expiresAfterHours, { min: 1, max: MAX_VOUCHER_EXPIRY_HOURS }),
    maxClaimsPerDevicePerCampaign: normalizePositiveInteger(
      source.maxClaimsPerDevicePerCampaign,
      normalizePositiveInteger(
        source.maxClaimsPerEmailPerCampaign,
        defaults.maxClaimsPerDevicePerCampaign,
        { min: 1, max: 10 }
      ),
      { min: 1, max: 10 }
    ),
    redemptionMethod: source.redemptionMethod === DEFAULT_VOUCHER_REDEMPTION_METHOD
      ? DEFAULT_VOUCHER_REDEMPTION_METHOD
      : defaults.redemptionMethod,
    backgroundColor: normalizeHexColor(source.backgroundColor ?? source.background_color, ''),
    textColor: normalizeHexColor(source.textColor ?? source.text_color, ''),
    landingBackgroundColor: normalizeHexColor(
      source.landingBackgroundColor ?? source.landing_background_color,
      ''
    ),
    landingTextColor: normalizeHexColor(
      source.landingTextColor ?? source.landing_text_color,
      ''
    ),
  };
};

const normalizeCampaignLandingTheme = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const defaults = buildDefaultCampaignLandingTheme();
  return {
    ...defaults,
    pageBackgroundColor: normalizeHexColor(
      source.pageBackgroundColor ?? source.page_background_color,
      ''
    ),
    pageBackgroundAccentColor: normalizeHexColor(
      source.pageBackgroundAccentColor ?? source.page_background_accent_color,
      ''
    ),
    panelBackgroundColor: normalizeHexColor(
      source.panelBackgroundColor ?? source.panel_background_color,
      ''
    ),
    sectionBackgroundColor: normalizeHexColor(
      source.sectionBackgroundColor ?? source.section_background_color,
      ''
    ),
    headingTextColor: normalizeHexColor(
      source.headingTextColor ?? source.heading_text_color,
      ''
    ),
    bodyTextColor: normalizeHexColor(
      source.bodyTextColor ?? source.body_text_color,
      ''
    ),
    accentColor: normalizeHexColor(
      source.accentColor ?? source.accent_color,
      ''
    ),
    mutedTextColor: normalizeHexColor(
      source.mutedTextColor ?? source.muted_text_color,
      ''
    ),
  };
};

const normalizeCampaignTranslationFields = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const voucherOffer = source.voucherOffer && typeof source.voucherOffer === 'object'
    ? source.voucherOffer
    : {};
  return {
    title: typeof source.title === 'string' ? source.title : '',
    landingPage: typeof source.landingPage === 'string' ? source.landingPage : '',
    voucherOffer: {
      headline: typeof voucherOffer.headline === 'string' ? voucherOffer.headline : '',
      body: typeof voucherOffer.body === 'string' ? voucherOffer.body : '',
      submitLabel: typeof voucherOffer.submitLabel === 'string' ? voucherOffer.submitLabel : '',
      offerTitle: typeof voucherOffer.offerTitle === 'string' ? voucherOffer.offerTitle : '',
      terms: typeof voucherOffer.terms === 'string' ? voucherOffer.terms : '',
    },
  };
};

const sortCampaignLanguageVersions = (rows, sourceLanguageCode) => (
  [...rows].sort((a, b) => {
    if (sameLanguageCode(a.languageCode, sourceLanguageCode)) return -1;
    if (sameLanguageCode(b.languageCode, sourceLanguageCode)) return 1;
    return String(a.languageCode || '').localeCompare(String(b.languageCode || ''));
  })
);

const normalizeCampaignLanguageVersions = ({
  campaign,
  title,
  content,
  translations,
  languageVersions,
  sourceLanguageCode,
}) => {
  const resolvedSourceLanguageCode = normalizeLanguageCode(
    sourceLanguageCode || campaign?.sourceLanguageCode || campaign?.source_language_code || content?.language,
    'en'
  );
  const sourceContentUpdatedAt = campaign?.translationSourceUpdatedAt
    || campaign?.translation_source_updated_at
    || campaign?.updatedAt
    || campaign?.updated_at
    || campaign?.createdAt
    || campaign?.created_at
    || null;
  const sourceVersion = {
    languageCode: resolvedSourceLanguageCode,
    isSource: true,
    translatedFields: normalizeCampaignTranslationFields({
      title,
      landingPage: content?.landingPage || '',
      voucherOffer: content?.voucherOffer || {},
    }),
    autoGenerated: false,
    reviewStatus: 'draft',
    freshness: 'source',
    isOutOfDate: false,
    sourceContentUpdatedAt,
    contentUpdatedAt: sourceContentUpdatedAt,
  };
  const rawRows = Array.isArray(languageVersions) && languageVersions.length
    ? languageVersions
    : Array.isArray(translations)
      ? translations
      : [];
  const normalizedRows = rawRows
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const languageCode = normalizeLanguageCode(row.languageCode || row.language_code);
      if (!languageCode || sameLanguageCode(languageCode, resolvedSourceLanguageCode)) return null;
      return {
        languageCode,
        isSource: false,
        translatedFields: normalizeCampaignTranslationFields(
          row.translatedFields || row.translated_fields || row.fields || {}
        ),
        autoGenerated: row.autoGenerated !== undefined ? Boolean(row.autoGenerated) : Boolean(row.auto_generated),
        reviewStatus: String(row.reviewStatus || row.review_status || 'draft').trim().toLowerCase() || 'draft',
        freshness: row.freshness || (row.isOutOfDate || row.is_out_of_date ? 'stale' : 'current'),
        isOutOfDate: row.isOutOfDate !== undefined ? Boolean(row.isOutOfDate) : Boolean(row.is_out_of_date),
        sourceContentHash: row.sourceContentHash || row.source_content_hash || null,
        sourceContentUpdatedAt: row.sourceContentUpdatedAt || row.source_content_updated_at || null,
        contentUpdatedAt: row.contentUpdatedAt || row.content_updated_at || row.updatedAt || row.updated_at || row.createdAt || row.created_at || null,
      };
    })
    .filter(Boolean);
  return sortCampaignLanguageVersions([sourceVersion, ...normalizedRows], resolvedSourceLanguageCode);
};

const formatVoucherClaimSummary = (maxClaimsPerDevicePerCampaign) => {
  const count = normalizePositiveInteger(maxClaimsPerDevicePerCampaign, 1, { min: 1, max: 10 });
  if (count === 1) return 'One claim per device per campaign.';
  return `Up to ${count} claims per device per campaign.`;
};

const splitVoucherExpiryHours = (value) => {
  const totalHours = normalizePositiveInteger(value, DEFAULT_VOUCHER_EXPIRY_HOURS, { min: 1, max: MAX_VOUCHER_EXPIRY_HOURS });
  const months = Math.floor(totalHours / HOURS_PER_MONTH);
  const afterMonths = totalHours - (months * HOURS_PER_MONTH);
  const days = Math.floor(afterMonths / HOURS_PER_DAY);
  const hours = afterMonths - (days * HOURS_PER_DAY);
  return { months, days, hours };
};

const clampVoucherExpiryPart = (value, fallback, { min = 0, max = Number.MAX_SAFE_INTEGER } = {}) => {
  const normalized = Number(value);
  if (!Number.isFinite(normalized)) return fallback;
  const rounded = Math.trunc(normalized);
  if (rounded < min) return min;
  if (rounded > max) return max;
  return rounded;
};

const composeVoucherExpiryHours = ({ months = 0, days = 0, hours = 0 } = {}) => {
  const normalizedMonths = clampVoucherExpiryPart(months, 0, { min: 0, max: 12 });
  const normalizedDays = clampVoucherExpiryPart(days, 0, { min: 0, max: DAYS_PER_MONTH - 1 });
  const normalizedHours = clampVoucherExpiryPart(hours, 0, { min: 0, max: HOURS_PER_DAY - 1 });
  const total = (normalizedMonths * HOURS_PER_MONTH) + (normalizedDays * HOURS_PER_DAY) + normalizedHours;
  return normalizePositiveInteger(total, DEFAULT_VOUCHER_EXPIRY_HOURS, { min: 1, max: MAX_VOUCHER_EXPIRY_HOURS });
};

const formatVoucherExpiryLabel = (value) => {
  const { months, days, hours } = splitVoucherExpiryHours(value);
  const parts = [];
  if (months > 0) parts.push(`${months} month${months === 1 ? '' : 's'}`);
  if (days > 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  if (hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  if (!parts.length) {
    return '1 hour';
  }
  return parts.join(' ');
};

const readBooleanSetting = (key, fallback = false) => {
  if (typeof window === 'undefined') return fallback;
  const stored = window.localStorage.getItem(key);
  if (stored === null) return fallback;
  return stored === 'true';
};

const writeBooleanSetting = (key, value) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, value ? 'true' : 'false');
};

const usePersistedFlag = (key, fallback = false) => {
  const [value, setValue] = useState(() => readBooleanSetting(key, fallback));

  useEffect(() => {
    setValue(readBooleanSetting(key, fallback));
  }, [key, fallback]);

  const update = useCallback(
    (next) => {
      setValue(next);
      writeBooleanSetting(key, next);
    },
    [key]
  );

  return [value, update];
};

function VoucherColorField({ label, value, onChange, disabled = false }) {
  return (
    <label className="block text-sm mb-1">
      <span className="block mb-1">{label}</span>
      <div className="flex items-center gap-3">
        <input
          type="color"
          className="h-11 w-14 rounded border border-gray-700 bg-gray-800 p-1"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
        <input
          type="text"
          className="w-full p-2 bg-gray-800 text-white rounded border border-gray-700 font-mono"
          value={value}
          readOnly
          disabled={disabled}
        />
      </div>
    </label>
  );
}

function buildTvUrl(landingCandidate, key, origin = '') {
  const candidate = typeof landingCandidate === 'string' ? landingCandidate.trim() : '';
  const resolvedOrigin = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  if (candidate && key) {
    const marker = `/c/${key}`;
    if (candidate.includes(marker)) {
      return candidate.replace(marker, `/tv/${key}`);
    }
  }
  if (candidate && candidate.includes('/lp/c/')) {
    return candidate.replace('/lp/c/', '/lp/tv/');
  }
  if (candidate) {
    try {
      const parsed = new URL(candidate);
      if (key) {
        return `${parsed.origin}/lp/tv/${key}`;
      }
      const parts = parsed.pathname.split('/').filter(Boolean);
      const idx = parts.indexOf('c');
      if (idx !== -1) {
        parts[idx] = 'tv';
        parsed.pathname = `/${parts.join('/')}`;
        return parsed.toString();
      }
      return `${parsed.origin}/lp/tv/${parts.pop() || ''}`;
    } catch (err) {
      /* ignore invalid URL */
    }
  }
  if (key) {
    if (resolvedOrigin) {
      return `${resolvedOrigin}/lp/tv/${key}`;
    }
    return `/lp/tv/${key}`;
  }
  return candidate || '';
}

const ARTWORK_ONLY_PROMPT_RULES = [
  'Create only the hero/background artwork for this campaign.',
  'Do not include any voucher, coupon, ticket, certificate, QR code, form, email field, button, app UI, webpage panel, device mockup, or overlaid marketing text inside the image.',
  'The landing page automatically renders any CTA, voucher form, and redemption UI separately below or around the image, so the image itself should stay clean and purely visual.',
].join('\n');

function derivePromptText(content = {}) {
  const sources = [];
  if (content.english && typeof content.english === 'object') {
    sources.push(content.english);
  }
  sources.push(content);

  for (const source of sources) {
    if (source && typeof source === 'object') {
      if (typeof source.landingPage === 'string' && source.landingPage.trim()) {
        return `${source.landingPage.trim()}\n\n${ARTWORK_ONLY_PROMPT_RULES}`;
      }
      if (typeof source.facebookPost === 'string' && source.facebookPost.trim()) {
        return `${source.facebookPost.trim()}\n\n${ARTWORK_ONLY_PROMPT_RULES}`;
      }
      const tv = source.tvContent || {};
      const tvParts = [tv.title, tv.subtitle, tv.message, tv.footer]
        .map((part) => (typeof part === 'string' ? part.trim() : ''))
        .filter(Boolean);
      if (tvParts.length) {
        return `${tvParts.join('\n')}\n\n${ARTWORK_ONLY_PROMPT_RULES}`;
      }
      if (typeof source.googleUpdate === 'string' && source.googleUpdate.trim()) {
        return `${source.googleUpdate.trim()}\n\n${ARTWORK_ONLY_PROMPT_RULES}`;
      }
    }
  }
  return ARTWORK_ONLY_PROMPT_RULES;
}

function normalizePromotionVenue(value) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    siteId: Number(source.siteId || 0) || null,
    siteName: source.siteName || '',
    businessType: source.businessType || '',
    primaryTypeDisplayName: source.primaryTypeDisplayName || '',
    promptMode: source.promptMode || '',
  };
}

function normalizePromotionContext(value) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    mode: source.mode || '',
    hostSiteId: Number(source.hostSiteId || 0) || null,
    promotedSiteIds: Array.isArray(source.promotedSiteIds)
      ? source.promotedSiteIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : [],
    sourceCatalogId: Number(source.sourceCatalogId || 0) || null,
    hostVenue: source.hostVenue ? normalizePromotionVenue(source.hostVenue) : null,
    promotedVenues: Array.isArray(source.promotedVenues)
      ? source.promotedVenues.map((venue) => normalizePromotionVenue(venue)).filter((venue) => venue.siteId || venue.siteName)
      : [],
    sourceCatalog: source.sourceCatalog && typeof source.sourceCatalog === 'object'
      ? {
        catalogId: Number(source.sourceCatalog.catalogId || source.sourceCatalog.id || 0) || null,
        title: source.sourceCatalog.title || '',
        siteName: source.sourceCatalog.siteName || '',
        areaName: source.sourceCatalog.areaName || '',
      }
      : null,
  };
}

function buildPromotionVenueLabel(venue) {
  if (!venue) return '';
  const typeLabel = String(venue.primaryTypeDisplayName || venue.businessType || '').trim();
  return typeLabel
    ? `${venue.siteName || 'Unknown site'} (${typeLabel})`
    : (venue.siteName || 'Unknown site');
}

function buildSourceCatalogSummary(sourceCatalog) {
  if (!sourceCatalog) return '';
  return [sourceCatalog.siteName, sourceCatalog.areaName, sourceCatalog.title].filter(Boolean).join(' • ');
}

export default function CampaignEditor({
  campaign,
  site,
  selectedDevice = null,
  assets = [],
  assetsReady = true,
  onCreateDesign,
  onSaved,
  onDeleted,
  onAnnouncementSaved,
  onOverlaySaved,
  onRefreshAssets,
  autoGenerateSignal,
  claimArtworkGenerationSignal,
  autoSaveSignal = 0,
  announcementPreset,
  generationProgress,
  onArtworkGenerationStart,
  onArtworkGenerationComplete,
  onArtworkGenerationError,
}) {
  const normalize = (value) => {
    const source = value && typeof value === 'object' ? value : {};
    const normalizeTv = (tvSource = {}) => ({
      title: tvSource.title || '',
      subtitle: tvSource.subtitle || '',
      message: tvSource.message || '',
      footer: tvSource.footer || '',
      qrLabel: typeof tvSource.qrLabel === 'string' ? tvSource.qrLabel : '',
    });

    const normalized = {
      tvContent: normalizeTv(source.tvContent || {}),
      selectedAssetId: source.selectedAssetId || null,
      heroAssetIds: normalizeCampaignHeroAssetIds(
        source.heroAssetIds ?? source.hero_asset_ids,
        source.selectedAssetId || null
      ),
      heroSlideshow: normalizeHeroSlideshow(source.heroSlideshow ?? source.hero_slideshow),
      relatedCampaignIds: Array.isArray(source.relatedCampaignIds) ? source.relatedCampaignIds : [],
      facebookPost: source.facebookPost || '',
      googleUpdate: source.googleUpdate || '',
      overlay: source.overlay || '',
      overlayId: source.overlayId || null,
      overlayImageUrl: source.overlayImageUrl || '',
      overlayQRCodeUrl: source.overlayQRCodeUrl || '',
      tvOverlayId: source.tvOverlayId || null,
      tvOverlayUrl: source.tvOverlayUrl || '',
      tvOverlayQRCodeUrl: source.tvOverlayQRCodeUrl || '',
      announcement: source.announcement || '',
      landingPage: source.landingPage || '',
      english: null,
      language: source.language || null,
      landingTheme: normalizeCampaignLandingTheme(source.landingTheme),
      voucherOffer: normalizeVoucherOffer(source.voucherOffer),
      promotionContext: normalizePromotionContext(source.promotionContext),
      socialSource: normalizeSocialSource(source.socialSource),
      facebookPublication: normalizeFacebookPublication(source.facebookPublication),
    };

    if (source.english && typeof source.english === 'object') {
      normalized.english = {
        title: source.english.title || '',
        tvContent: normalizeTv(source.english.tvContent || {}),
        facebookPost: source.english.facebookPost || '',
        googleUpdate: source.english.googleUpdate || '',
        overlay: source.english.overlay || '',
        announcement: source.english.announcement || '',
        landingPage: source.english.landingPage || '',
      };
    }

    return normalized;
  };

  const [data, setData] = useState(() => normalize(campaign.content || {}));
  const [title, setTitle] = useState(campaign.title || '');
  const [monetisationMode, setMonetisationMode] = useState(
    normalizeMonetisationMode(
      campaign.monetisationMode || campaign.monetisation_mode || site?.defaultMonetisationMode || site?.default_monetisation_mode || 'off',
      'off'
    )
  );
  const [sizes, setSizes] = useState({ tv: TV_OPTIONS[0].key, fb: FB_OPTIONS[0].key, gmb: GMB_OPTIONS[0].key });
  const [availableVoices, setAvailableVoices] = useState([]);
  const [uniqueLanguages, setUniqueLanguages] = useState([]);
  const [announcementSettings, setAnnouncementSettings] = useState({ language: 'en-AU', voice: 'en-AU-Wavenet-B' });
  const [announcementFolded, setAnnouncementFolded] = useState(false);
  const [overlayFolded, setOverlayFolded] = useState(false);
  const [overlayId, setOverlayId] = useState(null);
  const [landingUrl, setLandingUrl] = useState('');
  const [landingPreviewVersion, setLandingPreviewVersion] = useState(0);
  const [relatedCampaignsOpen, setRelatedCampaignsOpen] = useState(false);
  const [campaignAds, setCampaignAds] = useState([]);
  const [campaignAdsLoading, setCampaignAdsLoading] = useState(false);
  const [campaignAdsError, setCampaignAdsError] = useState('');
  const [campaignAdsFilter, setCampaignAdsFilter] = useState({ q: '' });
  const [advertisingTargets, setAdvertisingTargets] = useState({ groups: [] });
  const [advertisingTargetsLoading, setAdvertisingTargetsLoading] = useState(false);
  const [advertisingTargetsError, setAdvertisingTargetsError] = useState('');
  const [advertisingFilter, setAdvertisingFilter] = useState('');
  const [advertisingSelectionKeys, setAdvertisingSelectionKeys] = useState([]);
  const [savedAdvertisingSelectionKeys, setSavedAdvertisingSelectionKeys] = useState([]);
  const [tvUrl, setTvUrl] = useState('');
  const [hasDevices, setHasDevices] = useState(false);
  const [previewDeviceId, setPreviewDeviceId] = useState(null);
  const [selectedAssetId, setSelectedAssetId] = useState(() => {
    const normalized = normalize(campaign.content || {});
    return normalized.selectedAssetId || null;
  });
  const [adLayouts, setAdLayouts] = useState(() => ensureLayouts(campaign.adLayouts || {}));
  const [tvPreviewImageUrl, setTvPreviewImageUrl] = useState('');
  const [activePanel, setActivePanel] = useState(null);
  const [facebookConnection, setFacebookConnection] = useState(null);
  const [facebookAccountId, setFacebookAccountId] = useState('');
  const [facebookPublishState, setFacebookPublishState] = useState({ loading: false, error: '', publication: null });
  const [pendingGeneratedAssetId, setPendingGeneratedAssetId] = useState(null);
  const [queuedAutoSaveSignal, setQueuedAutoSaveSignal] = useState(0);
  const [translationSourceLanguageCode, setTranslationSourceLanguageCode] = useState(
    normalizeLanguageCode(campaign.sourceLanguageCode || campaign.source_language_code || campaign.content?.language, 'en')
  );
  const [campaignLanguageVersions, setCampaignLanguageVersions] = useState(() => normalizeCampaignLanguageVersions({
    campaign,
    title: campaign.title || '',
    content: normalize(campaign.content || {}),
    translations: campaign.translations || [],
    languageVersions: campaign.languageVersions || [],
    sourceLanguageCode: campaign.sourceLanguageCode || campaign.source_language_code || campaign.content?.language || 'en',
  }));
  const [selectedTranslationLanguageCode, setSelectedTranslationLanguageCode] = useState(() => {
    const initialSourceLanguageCode = normalizeLanguageCode(
      campaign.sourceLanguageCode || campaign.source_language_code || campaign.content?.language,
      'en'
    );
    const initialLanguageVersions = normalizeCampaignLanguageVersions({
      campaign,
      title: campaign.title || '',
      content: normalize(campaign.content || {}),
      translations: campaign.translations || [],
      languageVersions: campaign.languageVersions || [],
      sourceLanguageCode: initialSourceLanguageCode,
    });
    return initialLanguageVersions.find((row) => !sameLanguageCode(row.languageCode, initialSourceLanguageCode))?.languageCode || initialSourceLanguageCode;
  });
  const [manualTranslationLanguageCode, setManualTranslationLanguageCode] = useState('fr');
  const [translationError, setTranslationError] = useState('');
  const [translationMessage, setTranslationMessage] = useState('');
  const [translationMeta, setTranslationMeta] = useState(null);
  const [translatingLanguageCode, setTranslatingLanguageCode] = useState('');
  const [voucherUsageResetting, setVoucherUsageResetting] = useState(false);
  const [voucherUsageResetMessage, setVoucherUsageResetMessage] = useState('');
  const [voucherUsageResetError, setVoucherUsageResetError] = useState('');
  const storagePrefix = useMemo(() => `campaign:${campaign?.id || 'unsaved'}`, [campaign?.id]);
  const makeFoldKey = useCallback((section) => `${storagePrefix}:fold:${section}`, [storagePrefix]);
  const [imageFolded, setImageFolded] = usePersistedFlag(makeFoldKey('image'), false);
  const [tvFolded, setTvFolded] = usePersistedFlag(makeFoldKey('tv'), false);
  const [socialFolded, setSocialFolded] = usePersistedFlag(makeFoldKey('social'), false);
  const [landingFolded, setLandingFolded] = usePersistedFlag(makeFoldKey('landing'), false);
  const [advertisingFolded, setAdvertisingFolded] = usePersistedFlag(makeFoldKey('advertising'), false);
  const [landingThemeOpen, setLandingThemeOpen] = usePersistedFlag(makeFoldKey('landingTheme'), false);
  const [voucherSettingsOpen, setVoucherSettingsOpen] = usePersistedFlag(makeFoldKey('voucherOffer'), false);
  const photoBrowserRef = useRef(null);
  const handleSaveAllRef = useRef(null);
  const autoSaveInFlightRef = useRef(false);
  const token = localStorage.getItem('token');

  const imageAssets = useMemo(
    () => (Array.isArray(assets) ? assets.filter((asset) => isImageAsset(asset)) : []),
    [assets]
  );

  const selectedImageAsset = useMemo(
    () => imageAssets.find((asset) => asset.id === selectedAssetId) || null,
    [imageAssets, selectedAssetId]
  );
  const selectedHeroAssetIds = useMemo(
    () => normalizeCampaignHeroAssetIds(data.heroAssetIds, data.selectedAssetId || null),
    [data.heroAssetIds, data.selectedAssetId]
  );
  const selectedHeroAssets = useMemo(
    () => selectedHeroAssetIds
      .map((assetId) => imageAssets.find((asset) => String(asset.id) === String(assetId)) || null)
      .filter(Boolean),
    [imageAssets, selectedHeroAssetIds]
  );

  useEffect(() => {
    if (activePanel !== 'social' || !site?.id || facebookConnection) return;
    let cancelled = false;
    fetch(`/api/social/facebook/status?siteId=${encodeURIComponent(site.id)}`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    })
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || 'Failed to load Facebook Pages.');
        return body;
      })
      .then((body) => {
        if (cancelled) return;
        setFacebookConnection(body);
        const accounts = Array.isArray(body.accounts) ? body.accounts.filter((account) => account.enabled) : [];
        setFacebookAccountId((current) => current || String(accounts[0]?.id || ''));
      })
      .catch((error) => {
        if (!cancelled) setFacebookPublishState((current) => ({ ...current, error: error.message }));
      });
    return () => { cancelled = true; };
  }, [activePanel, facebookConnection, site?.id, token]);

  const relatedCampaignIds = useMemo(
    () => (Array.isArray(data.relatedCampaignIds) ? data.relatedCampaignIds : [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0),
    [data.relatedCampaignIds]
  );
  const savedRelatedCampaignIds = useMemo(
    () => (Array.isArray(campaign?.content?.relatedCampaignIds) ? campaign.content.relatedCampaignIds : [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0),
    [campaign?.content?.relatedCampaignIds]
  );
  const relatedCampaignCompositionChanged = useMemo(() => {
    const current = [...relatedCampaignIds].sort((a, b) => a - b);
    const saved = [...savedRelatedCampaignIds].sort((a, b) => a - b);
    if (current.length !== saved.length) return true;
    return current.some((id, index) => id !== saved[index]);
  }, [relatedCampaignIds, savedRelatedCampaignIds]);

  const availableRelatedCampaignAds = useMemo(
    () => (Array.isArray(campaignAds) ? campaignAds : []).filter((campaignAd) => (
      String(campaignAd?.campaignId || '') !== String(campaign?.id || '')
    )),
    [campaign?.id, campaignAds]
  );

  const filteredRelatedCampaignAds = useMemo(() => {
    const q = String(campaignAdsFilter.q || '').trim().toLowerCase();
    return availableRelatedCampaignAds.filter((campaignAd) => {
      if (!q) return true;
      return String(campaignAd.title || '').toLowerCase().includes(q)
        || String(campaignAd.siteName || '').toLowerCase().includes(q)
        || String(campaignAd.landingKey || '').toLowerCase().includes(q);
    });
  }, [availableRelatedCampaignAds, campaignAdsFilter]);

  const relatedCampaignAdsById = useMemo(() => {
    const map = new Map();
    availableRelatedCampaignAds.forEach((campaignAd) => {
      if (campaignAd?.campaignId) map.set(String(campaignAd.campaignId), campaignAd);
    });
    return map;
  }, [availableRelatedCampaignAds]);

  const selectedRelatedCampaignAds = useMemo(
    () => relatedCampaignIds
      .map((campaignId) => relatedCampaignAdsById.get(String(campaignId)) || null)
      .filter(Boolean),
    [relatedCampaignAdsById, relatedCampaignIds]
  );

  const promotionContext = useMemo(
    () => normalizePromotionContext(data.promotionContext),
    [data.promotionContext]
  );
  const hostCampaignLabel = useMemo(
    () => buildPromotionVenueLabel(
      promotionContext.hostVenue?.siteName
        ? promotionContext.hostVenue
        : {
          siteId: Number(site?.id || 0) || null,
          siteName: site?.name || '',
          businessType: site?.business_type || '',
          primaryTypeDisplayName: site?.primary_type_display_name || '',
        }
    ),
    [promotionContext.hostVenue, site?.business_type, site?.id, site?.name, site?.primary_type_display_name]
  );
  const promotedVenueLabels = useMemo(
    () => promotionContext.promotedVenues.map((venue) => buildPromotionVenueLabel(venue)).filter(Boolean),
    [promotionContext.promotedVenues]
  );
  const sourceCatalogSummary = useMemo(
    () => buildSourceCatalogSummary(promotionContext.sourceCatalog),
    [promotionContext.sourceCatalog]
  );

  const advertisingPlacementLookup = useMemo(() => {
    const map = new Map();
    const groups = Array.isArray(advertisingTargets?.groups) ? advertisingTargets.groups : [];
    groups.forEach((group) => {
      (Array.isArray(group?.sites) ? group.sites : []).forEach((siteGroup) => {
        (Array.isArray(siteGroup?.targets) ? siteGroup.targets : []).forEach((target) => {
          (Array.isArray(target?.placements) ? target.placements : []).forEach((placement) => {
            if (!placement?.selectionKey) return;
            map.set(String(placement.selectionKey), {
              ...placement,
              targetType: target.targetType,
              targetId: target.targetId,
              targetTitle: target.title,
              targetSubtitle: target.subtitle,
              targetLandingUrl: target.landingUrl,
              siteId: siteGroup.siteId,
              siteName: siteGroup.siteName,
              groupKey: group.key,
              groupLabel: group.label,
            });
          });
        });
      });
    });
    return map;
  }, [advertisingTargets]);

  const selectedAdvertisingPlacements = useMemo(
    () => advertisingSelectionKeys
      .map((selectionKey) => advertisingPlacementLookup.get(String(selectionKey)) || null)
      .filter(Boolean),
    [advertisingPlacementLookup, advertisingSelectionKeys]
  );

  const filteredAdvertisingGroups = useMemo(() => {
    const q = String(advertisingFilter || '').trim().toLowerCase();
    const groups = Array.isArray(advertisingTargets?.groups) ? advertisingTargets.groups : [];
    if (!q) return groups;
    return groups.map((group) => ({
      ...group,
      sites: (Array.isArray(group?.sites) ? group.sites : []).map((siteGroup) => ({
        ...siteGroup,
        targets: (Array.isArray(siteGroup?.targets) ? siteGroup.targets : []).filter((target) => {
          const targetMatches = [
            target?.title,
            target?.subtitle,
            target?.targetType,
            siteGroup?.siteName,
          ].some((value) => String(value || '').toLowerCase().includes(q));
          if (targetMatches) return true;
          return (Array.isArray(target?.placements) ? target.placements : []).some((placement) => (
            String(placement?.label || '').toLowerCase().includes(q)
              || String(placement?.summary || '').toLowerCase().includes(q)
              || String(placement?.beforeSectionName || '').toLowerCase().includes(q)
          ));
        }),
      })).filter((siteGroup) => Array.isArray(siteGroup.targets) && siteGroup.targets.length > 0),
    }));
  }, [advertisingFilter, advertisingTargets]);

  const siteTranslationLanguages = useMemo(() => {
    const suggestedLanguages = Array.isArray(campaign?.siteTranslationLanguages || campaign?.site_translation_languages)
      ? (campaign.siteTranslationLanguages || campaign.site_translation_languages)
      : [];
    const existingLanguages = campaignLanguageVersions
      .map((row) => normalizeLanguageCode(row.languageCode))
      .filter((languageCode) => languageCode && !sameLanguageCode(languageCode, translationSourceLanguageCode));
    return Array.from(new Set([...suggestedLanguages, ...existingLanguages]))
      .filter((languageCode) => !sameLanguageCode(languageCode, translationSourceLanguageCode))
      .sort((left, right) => left.localeCompare(right));
  }, [campaign?.siteTranslationLanguages, campaign?.site_translation_languages, campaignLanguageVersions, translationSourceLanguageCode]);

  const availableManualTranslationLanguages = useMemo(
    () => CONTENT_TRANSLATION_LANGUAGE_OPTIONS.filter((option) => !sameLanguageCode(option.code, translationSourceLanguageCode)),
    [translationSourceLanguageCode]
  );

  const translationLanguageVersionMap = useMemo(() => {
    const map = new Map();
    campaignLanguageVersions.forEach((row) => {
      const languageCode = normalizeLanguageCode(row.languageCode);
      if (languageCode) map.set(getLanguageKey(languageCode), row);
    });
    return map;
  }, [campaignLanguageVersions]);

  const selectedTranslationVersion = useMemo(
    () => translationLanguageVersionMap.get(getLanguageKey(selectedTranslationLanguageCode)) || null,
    [selectedTranslationLanguageCode, translationLanguageVersionMap]
  );

  const nonSourceCampaignLanguageVersions = useMemo(
    () => campaignLanguageVersions.filter((row) => !sameLanguageCode(row.languageCode, translationSourceLanguageCode)),
    [campaignLanguageVersions, translationSourceLanguageCode]
  );

  const selectedTranslationFields = useMemo(
    () => normalizeCampaignTranslationFields(selectedTranslationVersion?.translatedFields || {}),
    [selectedTranslationVersion]
  );
  const savedSourceLanguageCode = useMemo(
    () => normalizeLanguageCode(
      campaign?.sourceLanguageCode || campaign?.source_language_code || campaign?.content?.language,
      'en'
    ),
    [campaign?.content?.language, campaign?.sourceLanguageCode, campaign?.source_language_code]
  );
  const savedTranslationSourceFields = useMemo(
    () => normalizeCampaignTranslationFields({
      title: campaign?.title || '',
      landingPage: campaign?.content?.landingPage || '',
      voucherOffer: campaign?.content?.voucherOffer || {},
    }),
    [campaign?.content?.landingPage, campaign?.content?.voucherOffer, campaign?.title]
  );
  const currentTranslationSourceFields = useMemo(
    () => normalizeCampaignTranslationFields({
      title,
      landingPage: data?.landingPage || '',
      voucherOffer: data?.voucherOffer || {},
    }),
    [data?.landingPage, data?.voucherOffer, title]
  );
  const voucherTranslationScopeChanged = useMemo(
    () => JSON.stringify(currentTranslationSourceFields.voucherOffer) !== JSON.stringify(savedTranslationSourceFields.voucherOffer),
    [currentTranslationSourceFields.voucherOffer, savedTranslationSourceFields.voucherOffer]
  );
  const landingTranslationSourceChanged = useMemo(
    () => (
      currentTranslationSourceFields.title !== savedTranslationSourceFields.title
      || currentTranslationSourceFields.landingPage !== savedTranslationSourceFields.landingPage
    ),
    [
      currentTranslationSourceFields.landingPage,
      currentTranslationSourceFields.title,
      savedTranslationSourceFields.landingPage,
      savedTranslationSourceFields.title,
    ]
  );
  const translationSourceLanguageChanged = useMemo(
    () => !sameLanguageCode(savedSourceLanguageCode, translationSourceLanguageCode),
    [savedSourceLanguageCode, translationSourceLanguageCode]
  );
  const advertisingPlacementCompositionChanged = useMemo(() => {
    const current = [...new Set((Array.isArray(advertisingSelectionKeys) ? advertisingSelectionKeys : []).map((key) => String(key)))].sort();
    const saved = [...new Set((Array.isArray(savedAdvertisingSelectionKeys) ? savedAdvertisingSelectionKeys : []).map((key) => String(key)))].sort();
    if (current.length !== saved.length) return true;
    return current.some((key, index) => key !== saved[index]);
  }, [advertisingSelectionKeys, savedAdvertisingSelectionKeys]);
  const campaignTranslationReviewReasons = useMemo(() => {
    if (!nonSourceCampaignLanguageVersions.length) return [];
    const reasons = [];
    if (landingTranslationSourceChanged) reasons.push('Landing title or body changed');
    if (voucherTranslationScopeChanged) reasons.push('Voucher guest copy changed');
    if (relatedCampaignCompositionChanged) reasons.push('Related promotions changed');
    if (advertisingPlacementCompositionChanged) reasons.push('Advertising placements changed');
    if (translationSourceLanguageChanged) reasons.push('Source language changed');
    return reasons;
  }, [
    advertisingPlacementCompositionChanged,
    landingTranslationSourceChanged,
    nonSourceCampaignLanguageVersions.length,
    relatedCampaignCompositionChanged,
    translationSourceLanguageChanged,
    voucherTranslationScopeChanged,
  ]);
  const campaignTranslationReviewNeeded = campaignTranslationReviewReasons.length > 0;

  const loadCampaignAds = useCallback(async () => {
    setCampaignAdsLoading(true);
    setCampaignAdsError('');
    try {
      const res = await fetch('/api/creative/campaign-ads', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load campaign ads (${res.status})`);
      }
      const payload = await res.json().catch(() => ({}));
      setCampaignAds(Array.isArray(payload.campaignAds) ? payload.campaignAds : []);
    } catch (err) {
      console.error('Failed to load campaign ads', err);
      setCampaignAds([]);
      setCampaignAdsError(err.message || 'Failed to load campaign ads.');
    } finally {
      setCampaignAdsLoading(false);
    }
  }, [token]);

  const loadAdvertisingTargets = useCallback(async () => {
    if (!campaign?.id) {
      setAdvertisingTargets({ groups: [] });
      setAdvertisingSelectionKeys([]);
      setSavedAdvertisingSelectionKeys([]);
      setAdvertisingTargetsError('');
      setAdvertisingTargetsLoading(false);
      return;
    }
    setAdvertisingTargetsLoading(true);
    setAdvertisingTargetsError('');
    try {
      const res = await fetch(`/api/creative/campaigns/${campaign.id}/advertising-targets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load advertising targets (${res.status})`);
      }
      const payload = await res.json().catch(() => ({}));
      const groups = Array.isArray(payload.groups) ? payload.groups : [];
      setAdvertisingTargets({ groups });
      const nextSelectionKeys = [];
      groups.forEach((group) => {
        (Array.isArray(group?.sites) ? group.sites : []).forEach((siteGroup) => {
          (Array.isArray(siteGroup?.targets) ? siteGroup.targets : []).forEach((target) => {
            (Array.isArray(target?.placements) ? target.placements : []).forEach((placement) => {
              if (placement?.includesCurrent && placement?.selectionKey) {
                nextSelectionKeys.push(String(placement.selectionKey));
              }
            });
          });
        });
      });
      setAdvertisingSelectionKeys(nextSelectionKeys);
      setSavedAdvertisingSelectionKeys(nextSelectionKeys);
    } catch (err) {
      console.error('Failed to load advertising targets', err);
      setAdvertisingTargets({ groups: [] });
      setAdvertisingSelectionKeys([]);
      setSavedAdvertisingSelectionKeys([]);
      setAdvertisingTargetsError(err.message || 'Failed to load advertising targets.');
    } finally {
      setAdvertisingTargetsLoading(false);
    }
  }, [campaign?.id, token]);

  const defaultTemplateIds = useMemo(() => new Set(TV_TEMPLATES.map((tpl) => tpl.id)), []);
  const userTemplates = useMemo(() => {
    const templatesMap = adLayouts?.templates || {};
    return Object.entries(templatesMap)
      .filter(([id, layout]) => !defaultTemplateIds.has(id) || layout.isUserTemplate)
      .map(([id, layout]) => ({
        id,
        name: layout.templateName || id,
      }));
  }, [adLayouts, defaultTemplateIds]);

  const promptText = useMemo(() => derivePromptText(data), [data]);
  const siteDefaultMonetisationMode = useMemo(
    () => normalizeMonetisationMode(site?.defaultMonetisationMode || site?.default_monetisation_mode || 'off', 'off'),
    [site?.defaultMonetisationMode, site?.default_monetisation_mode]
  );
  const subscriptionType = useMemo(
    () => String(site?.subscriptionType || site?.subscription_type || getSubscriptionTypeFromToken(token) || 'free').trim().toLowerCase(),
    [site?.subscriptionType, site?.subscription_type, token]
  );
  const monetisationIsEnforced = subscriptionType === 'free';
  const effectiveMonetisationMode = monetisationIsEnforced
    ? 'full'
    : normalizeMonetisationMode(monetisationMode, siteDefaultMonetisationMode);

  const tvLayout = useMemo(() => ensureTvLayout(adLayouts.tv || {}), [adLayouts]);
  const selectedTemplateId = tvLayout.templateId || TV_TEMPLATES[0].id;

  useEffect(() => {
    if (!pendingGeneratedAssetId) return;
    if (imageAssets.some((asset) => String(asset.id) === String(pendingGeneratedAssetId))) {
      setPendingGeneratedAssetId(null);
    }
  }, [imageAssets, pendingGeneratedAssetId]);

  useEffect(() => {
    const reconciliation = reconcileCampaignHeroAssets({
      assetsReady,
      availableAssetIds: imageAssets.map((asset) => asset.id),
      heroAssetIds: data.heroAssetIds,
      selectedAssetId: data.selectedAssetId,
    });
    if (reconciliation.pending) return;
    if (
      pendingGeneratedAssetId &&
      selectedAssetId !== null &&
      String(selectedAssetId) === String(pendingGeneratedAssetId) &&
      !imageAssets.some((asset) => String(asset.id) === String(pendingGeneratedAssetId))
    ) {
      return;
    }
    const nextHeroIds = reconciliation.heroAssetIds;
    const nextSelectedAssetId = reconciliation.selectedAssetId;
    const currentSlideshow = normalizeHeroSlideshow(data.heroSlideshow);
    const nextSlideshow = nextHeroIds.length > 1
      ? currentSlideshow
      : { ...currentSlideshow, enabled: false };

    if (selectedAssetId !== nextSelectedAssetId) {
      setSelectedAssetId(nextSelectedAssetId);
    }
    if (
      data.selectedAssetId !== nextSelectedAssetId
      || JSON.stringify(normalizeCampaignHeroAssetIds(data.heroAssetIds)) !== JSON.stringify(nextHeroIds)
      || currentSlideshow.enabled !== nextSlideshow.enabled
    ) {
      setData((prev) => ({
        ...prev,
        selectedAssetId: nextSelectedAssetId,
        heroAssetIds: nextHeroIds,
        heroSlideshow: nextSlideshow,
      }));
    }
  }, [assetsReady, imageAssets, selectedAssetId, data.selectedAssetId, data.heroAssetIds, data.heroSlideshow, pendingGeneratedAssetId]);

  useEffect(() => {
    setAdLayouts(ensureLayouts(campaign.adLayouts || {}));
  }, [campaign.id, campaign.adLayouts]);

  useEffect(() => {
    let active = true;
    let objectUrl = null;
    (async () => {
      const links = await fetchSignedUrlForAsset(selectedImageAsset);
      if (!active) return;
      const token = localStorage.getItem('token');
      if (links?.previewUrl && token) {
        try {
          const res = await fetch(links.previewUrl, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) throw new Error(`Failed to load preview asset (${res.status})`);
          const blob = await res.blob();
          objectUrl = URL.createObjectURL(blob);
          if (active) {
            setTvPreviewImageUrl(objectUrl);
          }
          return;
        } catch (err) {
          console.error('Failed to fetch preview asset', err);
        }
      }
      const fallback = links?.signedUrl || data.overlayImageUrl || '';
      setTvPreviewImageUrl(fallback);
    })();
    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [selectedImageAsset, data.overlayImageUrl]);

  useEffect(() => {
    const normalizedData = normalize(campaign.content || {});
    const nextSourceLanguageCode = normalizeLanguageCode(
      campaign.sourceLanguageCode || campaign.source_language_code || normalizedData.language,
      'en'
    );
    const nextLanguageVersions = normalizeCampaignLanguageVersions({
      campaign,
      title: campaign.title || '',
      content: normalizedData,
      translations: campaign.translations || [],
      languageVersions: campaign.languageVersions || [],
      sourceLanguageCode: nextSourceLanguageCode,
    });
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const landingFromResponse = campaign.landingUrl || '';
    const landingFromKey = campaign.landingKey ? `${origin}/lp/c/${campaign.landingKey}` : '';
    const initialLanding = landingFromResponse || landingFromKey;
    const mergedData = {
      ...normalizedData,
      overlayQRCodeUrl: normalizedData.overlayQRCodeUrl || initialLanding,
    };
    setData(mergedData);
    setTitle(campaign.title || '');
    setMonetisationMode(
      normalizeMonetisationMode(
        campaign.monetisationMode || campaign.monetisation_mode || site?.defaultMonetisationMode || site?.default_monetisation_mode || 'off',
        'off'
      )
    );
    setSizes({ tv: TV_OPTIONS[0].key, fb: FB_OPTIONS[0].key, gmb: GMB_OPTIONS[0].key });
    setAnnouncementSettings({ language: 'en-AU', voice: 'en-AU-Wavenet-B' });
    setAnnouncementFolded(false);
    setOverlayFolded(Boolean(mergedData.overlayId));
    setOverlayId(mergedData.overlayId || null);
    const overlayLink = mergedData.overlayQRCodeUrl || '';
    const resolvedLanding = landingFromResponse || landingFromKey || overlayLink;
    const resolvedTv =
      buildTvUrl(landingFromResponse, campaign.landingKey, origin) ||
      buildTvUrl(overlayLink, campaign.landingKey, origin) ||
      buildTvUrl(landingFromKey, campaign.landingKey, origin);
    setLandingUrl(resolvedLanding || '');
    setTvUrl(resolvedTv || '');
    setSelectedAssetId(mergedData.selectedAssetId || null);
    setPendingGeneratedAssetId(null);
    setLandingPreviewVersion((version) => version + 1);
    setActivePanel(null);
    setTranslationSourceLanguageCode(nextSourceLanguageCode);
    setCampaignLanguageVersions(nextLanguageVersions);
    setSelectedTranslationLanguageCode(
      nextLanguageVersions.find((row) => !sameLanguageCode(row.languageCode, nextSourceLanguageCode))?.languageCode || nextSourceLanguageCode
    );
    setTranslationError('');
    setTranslationMessage('');
    setTranslationMeta(null);
    setVoucherUsageResetting(false);
    setVoucherUsageResetMessage('');
    setVoucherUsageResetError('');
  }, [campaign, site?.defaultMonetisationMode, site?.default_monetisation_mode]);

  useEffect(() => {
    if (!activePanel || typeof window === 'undefined') return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setActivePanel(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activePanel]);

  useEffect(() => {
    setCampaignLanguageVersions((prev) => normalizeCampaignLanguageVersions({
      campaign,
      title,
      content: data,
      translations: Array.isArray(prev) ? prev.filter((row) => !sameLanguageCode(row.languageCode, translationSourceLanguageCode)) : [],
      languageVersions: Array.isArray(prev) ? prev : [],
      sourceLanguageCode: translationSourceLanguageCode,
    }));
  }, [campaign, data, title, translationSourceLanguageCode]);

  useEffect(() => {
    if (campaignLanguageVersions.some((row) => sameLanguageCode(row.languageCode, selectedTranslationLanguageCode))) {
      return;
    }
    setSelectedTranslationLanguageCode(
      campaignLanguageVersions.find((row) => !sameLanguageCode(row.languageCode, translationSourceLanguageCode))?.languageCode
        || translationSourceLanguageCode
    );
  }, [campaignLanguageVersions, selectedTranslationLanguageCode, translationSourceLanguageCode]);

  useEffect(() => {
    if (availableManualTranslationLanguages.some((option) => sameLanguageCode(option.code, manualTranslationLanguageCode))) {
      return;
    }
    setManualTranslationLanguageCode(availableManualTranslationLanguages[0]?.code || 'en');
  }, [availableManualTranslationLanguages, manualTranslationLanguageCode]);

  useEffect(() => {
    const fetchOverlay = async () => {
      if (!overlayId) {
        setOverlayFolded(false);
        setData((prev) => ({ ...prev, overlayImageUrl: '', overlayQRCodeUrl: prev.overlayQRCodeUrl || '' }));
        return;
      }
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`/api/overlays/${overlayId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const ov = await res.json();
          setData((prev) => ({
            ...prev,
            overlay: ov.message || prev.overlay,
            overlayId,
            overlayImageUrl: ov.image_url || '',
            overlayQRCodeUrl: ov.qr_code_url || prev.overlayQRCodeUrl || '',
          }));
          if (ov.qr_code_url) {
            setLandingUrl((prev) => prev || ov.qr_code_url);
          }
          setOverlayFolded(true);
        }
      } catch (e) {
        console.error('Error fetching overlay details:', e);
      }
    };
    fetchOverlay();
  }, [overlayId]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const fetchVoices = async () => {
      try {
        const res = await fetch('/api/tts/voices', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch voices');
        const voicesData = await res.json();
        setAvailableVoices(voicesData || []);
        const langs = new Set();
        voicesData.forEach((voice) => {
          voice.languageCodes.forEach((code) => langs.add(code));
        });
        setUniqueLanguages([...langs]);
      } catch (err) {
        console.error('Error fetching voices:', err);
      }
    };
    fetchVoices();
  }, []);

  useEffect(() => {
    loadCampaignAds();
  }, [loadCampaignAds]);

  useEffect(() => {
    loadAdvertisingTargets();
  }, [loadAdvertisingTargets]);

  useEffect(() => {
    if (selectedDevice?.id) {
      setHasDevices(true);
      setPreviewDeviceId(selectedDevice.id);
      return undefined;
    }
    const fetchDevices = async () => {
      if (!site?.areas?.length) {
        setHasDevices(false);
        setPreviewDeviceId(null);
        return;
      }
      const token = localStorage.getItem('token');
      for (const area of site.areas) {
        try {
          const res = await fetch(`/api/areas/${area.id}/devices`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const d = await res.json();
            if (d.devices && d.devices.length) {
              setHasDevices(true);
              setPreviewDeviceId(d.devices[0].id);
              return;
            }
          }
        } catch (e) {
          /* ignore */
        }
      }
      setHasDevices(false);
      setPreviewDeviceId(null);
    };
    fetchDevices();
  }, [site, selectedDevice]);

  const playOverlayOnPreviewDevice = useCallback(async (overlayIdToPlay) => {
    const res = await fetch('/api/overlays/play', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ deviceId: previewDeviceId, overlayId: overlayIdToPlay }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.error || `Failed to play overlay (${res.status})`);
    }
  }, [previewDeviceId, token]);

  useEffect(() => {
    if (!autoGenerateSignal) return;
    if (
      typeof claimArtworkGenerationSignal === 'function' &&
      !claimArtworkGenerationSignal(autoGenerateSignal)
    ) {
      return;
    }
    let active = true;
    const run = async () => {
      const generator = photoBrowserRef.current?.generateNewImage;
      if (typeof generator !== 'function') return;
      if (typeof onArtworkGenerationStart === 'function') {
        onArtworkGenerationStart();
      }
      try {
        const generatedAsset = await generator();
        if (!active) return;
        const nextId = generatedAsset?.id || null;
        if (nextId) {
          setPendingGeneratedAssetId(nextId);
          setSelectedAssetId(nextId);
          setData((prev) => ({ ...prev, selectedAssetId: nextId }));
          if (typeof onArtworkGenerationComplete === 'function') {
            onArtworkGenerationComplete({ assetId: nextId });
          }
        } else if (typeof onArtworkGenerationError === 'function') {
          onArtworkGenerationError({ message: 'Artwork generation did not return an asset.' });
        }
      } catch (err) {
        if (!active) return;
        if (typeof onArtworkGenerationError === 'function') {
          onArtworkGenerationError({ error: err });
        }
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [
    autoGenerateSignal,
    claimArtworkGenerationSignal,
    onArtworkGenerationStart,
    onArtworkGenerationComplete,
    onArtworkGenerationError,
  ]);

  useEffect(() => {
    if (!autoSaveSignal) return;
    setQueuedAutoSaveSignal(autoSaveSignal);
  }, [autoSaveSignal]);

  const autoSaveReady = useMemo(() => {
    if (generationProgress?.autoGenerateArtwork && (generationProgress?.isLocked || pendingGeneratedAssetId)) {
      return false;
    }
    if (imageAssets.length > 0 && selectedAssetId === null) {
      return false;
    }
    return true;
  }, [
    generationProgress?.autoGenerateArtwork,
    generationProgress?.isLocked,
    imageAssets.length,
    pendingGeneratedAssetId,
    selectedAssetId,
  ]);

  useEffect(() => {
    if (!announcementPreset?.language) return;
    setAnnouncementSettings((prev) => {
      if (
        prev.language === announcementPreset.language &&
        (announcementPreset.voice ? prev.voice === announcementPreset.voice : prev.voice === '')
      ) {
        return prev;
      }
      return {
        language: announcementPreset.language,
        voice: announcementPreset.voice || '',
      };
    });
  }, [announcementPreset]);

  const landingPreviewSrc = useMemo(() => {
    if (!landingUrl) return '';
    if (!landingPreviewVersion) return landingUrl;
    const separator = landingUrl.includes('?') ? '&' : '?';
    return `${landingUrl}${separator}preview=${landingPreviewVersion}`;
  }, [landingUrl, landingPreviewVersion]);

  const updateTv = (field, value) => {
    setData((prev) => ({ ...prev, tvContent: { ...prev.tvContent, [field]: value } }));
  };

  const updateField = (key, value) => {
    if (['overlay', 'overlayImageUrl', 'overlayQRCodeUrl'].includes(key)) {
      setOverlayFolded(false);
    }
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const updateSocialQrDestination = (qrDestination) => {
    if (!data.socialSource) return;
    const normalizedDestination = qrDestination === 'landing_page' ? 'landing_page' : 'original_post';
    const qrUrl = normalizedDestination === 'landing_page'
      ? landingUrl
      : data.socialSource.permalink;
    setData((prev) => ({
      ...prev,
      overlayQRCodeUrl: qrUrl || '',
      socialSource: { ...prev.socialSource, qrDestination: normalizedDestination },
    }));
    setOverlayFolded(false);
  };

  const handleTranslationSourceLanguageChange = useCallback((value) => {
    const nextLanguageCode = normalizeLanguageCode(value, 'en');
    setTranslationSourceLanguageCode(nextLanguageCode);
    setData((prev) => ({ ...prev, language: nextLanguageCode }));
    if (!sameLanguageCode(selectedTranslationLanguageCode, nextLanguageCode)) return;
    setSelectedTranslationLanguageCode(
      nonSourceCampaignLanguageVersions.find((row) => !sameLanguageCode(row.languageCode, nextLanguageCode))?.languageCode || nextLanguageCode
    );
  }, [nonSourceCampaignLanguageVersions, selectedTranslationLanguageCode]);

  const updateVoucherOfferField = useCallback((key, value) => {
    setData((prev) => ({
      ...prev,
      voucherOffer: {
        ...normalizeVoucherOffer(prev.voucherOffer),
        [key]: value,
      },
    }));
  }, []);

  const updateLandingThemeField = useCallback((key, value) => {
    setData((prev) => ({
      ...prev,
      landingTheme: {
        ...normalizeCampaignLandingTheme(prev.landingTheme),
        [key]: value,
      },
    }));
  }, []);

  const updateVoucherExpiryPart = useCallback((key, value) => {
    setData((prev) => {
      const currentVoucherOffer = normalizeVoucherOffer(prev.voucherOffer);
      const currentParts = splitVoucherExpiryHours(currentVoucherOffer.expiresAfterHours);
      const nextParts = {
        ...currentParts,
        [key]: clampVoucherExpiryPart(
          value,
          currentParts[key],
          key === 'months'
            ? { min: 0, max: 12 }
            : key === 'days'
              ? { min: 0, max: DAYS_PER_MONTH - 1 }
              : { min: 0, max: HOURS_PER_DAY - 1 }
        ),
      };
      return {
        ...prev,
        voucherOffer: {
          ...currentVoucherOffer,
          expiresAfterHours: composeVoucherExpiryHours(nextParts),
        },
      };
    });
  }, []);

  const toggleRelatedCampaignSelection = useCallback((campaignId) => {
    const normalizedCampaignId = Number(campaignId || 0) || null;
    if (!normalizedCampaignId) return;
    setData((prev) => {
      const current = Array.isArray(prev.relatedCampaignIds) ? prev.relatedCampaignIds : [];
      const exists = current.some((id) => String(id) === String(normalizedCampaignId));
      return {
        ...prev,
        relatedCampaignIds: exists
          ? current.filter((id) => String(id) !== String(normalizedCampaignId))
          : [...current, normalizedCampaignId],
      };
    });
  }, []);

  const toggleAdvertisingSelection = useCallback((selectionKey) => {
    const normalizedKey = String(selectionKey || '').trim();
    if (!normalizedKey) return;
    setAdvertisingSelectionKeys((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (current.includes(normalizedKey)) {
        return current.filter((entry) => entry !== normalizedKey);
      }
      return [...current, normalizedKey];
    });
  }, []);

  const handleAssetSelect = (asset) => {
    const id = asset?.id ?? null;
    if (selectedAssetId !== id) {
      setSelectedAssetId(id);
    }
    setData((prev) => {
      const currentHeroIds = normalizeCampaignHeroAssetIds(prev.heroAssetIds, prev.selectedAssetId || null);
      const nextHeroIds = id
        ? [id, ...currentHeroIds.filter((entry) => String(entry) !== String(id))]
        : [];
      if (
        prev.selectedAssetId === id
        && JSON.stringify(currentHeroIds) === JSON.stringify(nextHeroIds)
      ) {
        return prev;
      }
      return {
        ...prev,
        selectedAssetId: id,
        heroAssetIds: nextHeroIds,
      };
    });
  };

  const handleToggleHeroAsset = useCallback((asset) => {
    const id = Number(asset?.id || 0) || null;
    if (!id) return;
    setData((prev) => {
      const currentHeroIds = normalizeCampaignHeroAssetIds(prev.heroAssetIds, prev.selectedAssetId || null);
      const exists = currentHeroIds.some((entry) => String(entry) === String(id));
      const nextHeroIds = exists
        ? currentHeroIds.filter((entry) => String(entry) !== String(id))
        : [...currentHeroIds, id];
      const nextSelectedAssetId = nextHeroIds[0] || null;
      const currentSlideshow = normalizeHeroSlideshow(prev.heroSlideshow);
      const nextSlideshow = nextHeroIds.length > 1
        ? {
            ...currentSlideshow,
            enabled: exists ? currentSlideshow.enabled : true,
          }
        : { ...currentSlideshow, enabled: false };
      if (
        JSON.stringify(currentHeroIds) === JSON.stringify(nextHeroIds)
        && prev.selectedAssetId === nextSelectedAssetId
        && currentSlideshow.enabled === nextSlideshow.enabled
      ) {
        return prev;
      }
      if (selectedAssetId !== nextSelectedAssetId) {
        setSelectedAssetId(nextSelectedAssetId);
      }
      return {
        ...prev,
        selectedAssetId: nextSelectedAssetId,
        heroAssetIds: nextHeroIds,
        heroSlideshow: nextSlideshow,
      };
    });
  }, [selectedAssetId]);

  const moveHeroAsset = useCallback((assetId, direction) => {
    const normalizedId = Number(assetId || 0) || null;
    if (!normalizedId || !direction) return;
    setData((prev) => {
      const currentHeroIds = normalizeCampaignHeroAssetIds(prev.heroAssetIds, prev.selectedAssetId || null);
      const index = currentHeroIds.findIndex((entry) => String(entry) === String(normalizedId));
      if (index === -1) return prev;
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= currentHeroIds.length) return prev;
      const nextHeroIds = currentHeroIds.slice();
      const [moved] = nextHeroIds.splice(index, 1);
      nextHeroIds.splice(targetIndex, 0, moved);
      const nextSelectedAssetId = nextHeroIds[0] || null;
      if (selectedAssetId !== nextSelectedAssetId) {
        setSelectedAssetId(nextSelectedAssetId);
      }
      return {
        ...prev,
        selectedAssetId: nextSelectedAssetId,
        heroAssetIds: nextHeroIds,
      };
    });
  }, [selectedAssetId]);

  const updateHeroSlideshowField = useCallback((key, value) => {
    setData((prev) => ({
      ...prev,
      heroSlideshow: {
        ...normalizeHeroSlideshow(prev.heroSlideshow),
        [key]: value,
      },
    }));
  }, []);

  const fetchSignedUrlForAsset = async (asset) => {
    if (!asset) return null;
    const previewKind = resolvePreviewKind(asset);
    if (!previewKind) return { signedUrl: null, previewUrl: null, contentType: null };
    const token = localStorage.getItem('token');
    const previewUrl = `/api/creative/assets/${asset.id}/files/${encodeURIComponent(previewKind)}`;
    try {
      const res = await fetch(
        `/api/creative/assets/${asset.id}/files/${encodeURIComponent(previewKind)}/url`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error(`Failed to presign asset (${res.status})`);
      const body = await res.json().catch(() => ({}));
      return {
        signedUrl: body?.url || null,
        previewUrl: body?.previewUrl || previewUrl,
        contentType: body?.contentType || null,
      };
    } catch (err) {
      console.error('Failed to retrieve signed image URL for overlay', err);
      return {
        signedUrl: null,
        previewUrl,
        contentType: null,
      };
    }
  };

  const updateTvLayout = useCallback((mutator) => {
    setAdLayouts((prev) => {
      const normalized = ensureLayouts(prev);
      const current = ensureTvLayout(normalized.tv);
      const draft = deepClone(current);
      const mutated = mutator ? mutator(draft) || draft : draft;
      const updatedTv = ensureTvLayout(mutated);
      return { ...normalized, tv: updatedTv };
    });
  }, []);

  const handleTemplateSelect = useCallback((templateId) => {
    setAdLayouts((prev) => {
      const normalized = ensureLayouts(prev);
      const nextLayout = ensureTvLayout(getTemplateById(templateId, normalized));
      return { ...normalized, tv: { ...nextLayout, templateId } };
    });
  }, []);

  const handleSaveNewTemplate = useCallback(() => {
    if (typeof window === 'undefined') return;
    const name = window.prompt('Enter a name for your template:');
    if (!name || !name.trim()) return;
    const trimmedName = name.trim();
    setAdLayouts((prev) => {
      const normalized = ensureLayouts(prev);
      const baseLayout = ensureTvLayout(deepClone(normalized.tv));
      const slugBase = trimmedName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'template';
      const owner = site?.id ? `site-${site.id}` : 'shared';
      let suffix = 1;
      let templateId = `user-${owner}-${slugBase}`;
      while (normalized.templates[templateId]) {
        templateId = `user-${owner}-${slugBase}-${suffix++}`;
      }
      const storedTemplate = { ...baseLayout, templateId, templateName: trimmedName, isUserTemplate: true };
      const updatedTemplates = { ...normalized.templates, [templateId]: storedTemplate };
      return { ...normalized, templates: updatedTemplates, tv: storedTemplate };
    });
    alert('Template saved. Press Save to persist changes to this campaign.');
  }, [setAdLayouts, site?.id]);

  const handleSizeChange = (field, value) => {
    setSizes((s) => ({ ...s, [field]: value }));
  };

  const handleDesign = (key) => {
    const sizeKeyMap = { tvContent: 'tv', facebookPost: 'fb', googleUpdate: 'gmb' };
    const optionsMap = { tv: TV_OPTIONS, fb: FB_OPTIONS, gmb: GMB_OPTIONS };
    const sizeKey = sizes[sizeKeyMap[key]];
    const option = optionsMap[sizeKeyMap[key]].find((o) => o.key === sizeKey);
    const text = data[key];
    onCreateDesign(typeof text === 'string' ? text : JSON.stringify(text), option);
  };

  const updateAnnouncementSetting = (field, value) => {
    setAnnouncementSettings((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'language') updated.voice = '';
      return updated;
    });
  };

  const getVoiceDisplayName = (voiceName, language) => {
    if (language && voiceName.startsWith(language + '-')) {
      return voiceName.replace(language + '-', '');
    }
    return voiceName;
  };

  const updateTranslationLanguageVersion = useCallback((languageCode, patch) => {
    const normalizedLanguageCode = normalizeLanguageCode(languageCode);
    if (!normalizedLanguageCode || sameLanguageCode(normalizedLanguageCode, translationSourceLanguageCode)) return;
    setCampaignLanguageVersions((prev) => {
      const nextRows = Array.isArray(prev) ? [...prev] : [];
      const currentIndex = nextRows.findIndex((row) => sameLanguageCode(row.languageCode, normalizedLanguageCode));
      const existingRow = currentIndex >= 0 ? nextRows[currentIndex] : null;
      const nextRow = {
        languageCode: normalizedLanguageCode,
        isSource: false,
        autoGenerated: existingRow?.autoGenerated || false,
        reviewStatus: existingRow?.reviewStatus || 'draft',
        freshness: existingRow?.freshness || 'current',
        isOutOfDate: existingRow?.isOutOfDate || false,
        translatedFields: normalizeCampaignTranslationFields({
          ...(existingRow?.translatedFields || {}),
          ...(patch?.translatedFields || {}),
          voucherOffer: {
            ...(existingRow?.translatedFields?.voucherOffer || {}),
            ...(patch?.translatedFields?.voucherOffer || {}),
          },
        }),
        ...patch,
      };
      if (currentIndex >= 0) {
        nextRows[currentIndex] = nextRow;
      } else {
        nextRows.push(nextRow);
      }
      return sortCampaignLanguageVersions(nextRows, translationSourceLanguageCode);
    });
  }, [translationSourceLanguageCode]);

  const addTranslationLanguage = useCallback((languageCode, { copySource = false } = {}) => {
    const normalizedLanguageCode = normalizeLanguageCode(languageCode);
    if (!normalizedLanguageCode || sameLanguageCode(normalizedLanguageCode, translationSourceLanguageCode)) return;
    const sourceRow = campaignLanguageVersions.find((row) => sameLanguageCode(row.languageCode, translationSourceLanguageCode));
    updateTranslationLanguageVersion(normalizedLanguageCode, {
      translatedFields: copySource
        ? normalizeCampaignTranslationFields(sourceRow?.translatedFields || {})
        : normalizeCampaignTranslationFields({}),
      autoGenerated: false,
      reviewStatus: 'draft',
      freshness: 'current',
      isOutOfDate: false,
    });
    setSelectedTranslationLanguageCode(normalizedLanguageCode);
    setTranslationError('');
    setTranslationMessage('');
  }, [campaignLanguageVersions, translationSourceLanguageCode, updateTranslationLanguageVersion]);

  const removeTranslationLanguage = useCallback((languageCode) => {
    const normalizedLanguageCode = normalizeLanguageCode(languageCode);
    if (!normalizedLanguageCode || sameLanguageCode(normalizedLanguageCode, translationSourceLanguageCode)) return;
    setCampaignLanguageVersions((prev) => sortCampaignLanguageVersions(
      (Array.isArray(prev) ? prev : []).filter((row) => !sameLanguageCode(row.languageCode, normalizedLanguageCode)),
      translationSourceLanguageCode
    ));
    if (sameLanguageCode(selectedTranslationLanguageCode, normalizedLanguageCode)) {
      setSelectedTranslationLanguageCode(translationSourceLanguageCode);
    }
    setTranslationError('');
    setTranslationMessage('');
  }, [selectedTranslationLanguageCode, translationSourceLanguageCode]);

  const handleTranslateLanguage = useCallback(async (languageCode) => {
    const normalizedLanguageCode = normalizeLanguageCode(languageCode);
    if (!normalizedLanguageCode || sameLanguageCode(normalizedLanguageCode, translationSourceLanguageCode)) return;
    setTranslationError('');
    setTranslationMessage('');
    setTranslatingLanguageCode(normalizedLanguageCode);
    try {
      const savedContent = await handleSaveAllRef.current?.({
        silentAnnouncement: true,
        persistAnnouncement: false,
      });
      if (!savedContent) {
        throw new Error('Save the campaign before translating.');
      }
      const res = await fetch(`/api/creative/campaigns/${campaign.id}/translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          languages: [normalizedLanguageCode],
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || `Translate failed (${res.status})`);
      }
      const updatedCampaign = payload?.campaign || null;
      if (updatedCampaign) {
        setTranslationMeta({
          provider: payload?.provider || '',
          model: payload?.model || '',
        });
        setTranslationMessage(
          `Updated ${getLanguageLabel(normalizedLanguageCode)} with ${payload?.provider || 'poly'}${payload?.model ? ` • ${payload.model}` : ''}.`
        );
        setSelectedTranslationLanguageCode(normalizedLanguageCode);
        if (typeof onSaved === 'function') onSaved(updatedCampaign);
      }
    } catch (err) {
      console.error('Failed to translate campaign', err);
      setTranslationError(err.message || 'Translate failed.');
    } finally {
      setTranslatingLanguageCode('');
    }
  }, [campaign.id, onSaved, token, translationSourceLanguageCode]);

  const updateSelectedTranslationFields = useCallback((patch) => {
    if (!selectedTranslationVersion || selectedTranslationVersion.isSource) return;
    updateTranslationLanguageVersion(selectedTranslationVersion.languageCode, {
      translatedFields: normalizeCampaignTranslationFields({
        ...selectedTranslationFields,
        ...patch,
        voucherOffer: {
          ...selectedTranslationFields.voucherOffer,
          ...(patch?.voucherOffer || {}),
        },
      }),
      reviewStatus: 'draft',
      freshness: 'current',
      isOutOfDate: false,
    });
  }, [selectedTranslationFields, selectedTranslationVersion, updateTranslationLanguageVersion]);

  const copySourceIntoSelectedTranslation = useCallback(() => {
    if (!selectedTranslationVersion || selectedTranslationVersion.isSource) return;
    const sourceRow = campaignLanguageVersions.find((row) => sameLanguageCode(row.languageCode, translationSourceLanguageCode));
    updateTranslationLanguageVersion(selectedTranslationVersion.languageCode, {
      translatedFields: normalizeCampaignTranslationFields(sourceRow?.translatedFields || {}),
      autoGenerated: false,
      reviewStatus: 'draft',
      freshness: 'current',
      isOutOfDate: false,
    });
    setTranslationMessage(`Copied ${getLanguageLabel(translationSourceLanguageCode)} into ${getLanguageLabel(selectedTranslationVersion.languageCode)}.`);
    setTranslationError('');
  }, [campaignLanguageVersions, selectedTranslationVersion, translationSourceLanguageCode, updateTranslationLanguageVersion]);

  const handleResetVoucherUsageBlocks = useCallback(async () => {
    if (!campaign?.id) return;
    const confirmed = typeof window === 'undefined'
      ? true
      : window.confirm(
        'Reset usage blocks for this campaign? Existing voucher claims for this campaign will be voided so the same phones can download another voucher.'
      );
    if (!confirmed) return;

    setVoucherUsageResetting(true);
    setVoucherUsageResetMessage('');
    setVoucherUsageResetError('');
    try {
      const res = await fetch(`/api/creative/campaigns/${campaign.id}/reset-voucher-usage-blocks`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || `Failed to reset usage blocks (${res.status})`);
      }
      setVoucherUsageResetMessage(payload?.message || 'Voucher usage blocks reset.');
    } catch (err) {
      console.error('Failed to reset voucher usage blocks', err);
      setVoucherUsageResetError(err.message || 'Failed to reset voucher usage blocks.');
    } finally {
      setVoucherUsageResetting(false);
    }
  }, [campaign?.id, token]);

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Please enter a campaign title!');
      return false;
    }
    try {
      const layoutsToSave = ensureLayouts(adLayouts || {});
      let contentToSave = {
        ...data,
        language: translationSourceLanguageCode,
        overlayId: overlayId || data.overlayId || null,
      };
      let updatedLandingUrl = landingUrl;
      const normalizedLanguageVersions = normalizeCampaignLanguageVersions({
        campaign,
        title,
        content: contentToSave,
        translations: campaign.translations || [],
        languageVersions: campaignLanguageVersions,
        sourceLanguageCode: translationSourceLanguageCode,
      });
      const advertisingSelections = selectedAdvertisingPlacements.map((placement) => ({
        selectionType: placement.selectionType,
        campaignSectionId: placement.campaignSectionId || null,
        catalogId: placement.catalogId || null,
        beforeSectionId: placement.beforeSectionId || null,
        targetCampaignId: placement.targetCampaignId || null,
      }));
      const assetLinks = await fetchSignedUrlForAsset(selectedImageAsset);
      const signedImageUrl = assetLinks?.signedUrl;
      if (signedImageUrl) {
        contentToSave = { ...contentToSave, overlayImageUrl: signedImageUrl };
      }
      const res = await fetch(`/api/creative/campaigns/${campaign.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title,
          content: contentToSave,
          adLayouts: layoutsToSave,
          monetisationMode: effectiveMonetisationMode,
          advertisingSelections,
          languageVersions: normalizedLanguageVersions,
        }),
      });
      if (!res.ok) {
        throw new Error(`Failed to save campaign (${res.status})`);
      }
      if (res.ok) {
        const payload = await res.json().catch(() => ({}));
        const updatedCampaign = payload?.campaign || null;
        setAdLayouts(layoutsToSave);
        if (signedImageUrl) {
          setData((prev) => ({ ...prev, overlayImageUrl: signedImageUrl }));
        }
        try {
          const ovRes = await fetch(`/api/creative/campaigns/${campaign.id}/generate-overlay`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (ovRes.ok) {
            const resp = await ovRes.json().catch(() => ({}));
            if (resp.overlay && resp.overlay.id) {
              const overlayData = resp.overlay;
              setOverlayId(overlayData.id);
              setOverlayFolded(true);
              const overlayImage = overlayData.image_url || '';
              const overlayQr = overlayData.qr_code_url || '';
              setData((prev) => ({
                ...prev,
                overlayId: overlayData.id,
                overlayImageUrl: overlayImage,
                overlayQRCodeUrl: overlayQr,
              }));
              contentToSave = {
                ...contentToSave,
                overlayId: overlayData.id,
                overlayImageUrl: overlayImage,
                overlayQRCodeUrl: overlayQr,
              };
            }
            if (resp.fullOverlay && resp.fullOverlay.id) {
              const fullOverlay = resp.fullOverlay;
              const fullImage = fullOverlay.image_url || '';
              const fullQr = fullOverlay.qr_code_url || '';
              setData((prev) => ({
                ...prev,
                tvOverlayId: fullOverlay.id,
                tvOverlayUrl: fullImage,
                tvOverlayQRCodeUrl: fullQr,
              }));
              contentToSave = {
                ...contentToSave,
                tvOverlayId: fullOverlay.id,
                tvOverlayUrl: fullImage,
                tvOverlayQRCodeUrl: fullQr,
              };
            }
            if (resp.landingUrl) {
              updatedLandingUrl = resp.landingUrl;
              setLandingUrl(resp.landingUrl);
              setData((prev) => ({
                ...prev,
                overlayQRCodeUrl: resp.landingUrl,
              }));
              contentToSave = {
                ...contentToSave,
                overlayQRCodeUrl: resp.landingUrl,
              };
              const nextTvUrl = buildTvUrl(
                resp.landingUrl,
                campaign.landingKey,
                typeof window !== 'undefined' ? window.location.origin : ''
              );
              if (nextTvUrl) {
                setTvUrl(nextTvUrl);
              }
            }
            if (resp.tvLandingUrl) {
              setTvUrl(resp.tvLandingUrl);
              setData((prev) => ({ ...prev, tvOverlayUrl: resp.tvLandingUrl }));
              contentToSave = {
                ...contentToSave,
                tvOverlayUrl: resp.tvLandingUrl,
              };
            }
          }
        } catch (e) {
          console.error('Failed to generate landing overlay', e);
        }
        const refreshedTvUrl = buildTvUrl(
          updatedLandingUrl,
          campaign.landingKey,
          typeof window !== 'undefined' ? window.location.origin : ''
        );
        if (refreshedTvUrl) {
          setTvUrl(refreshedTvUrl);
        }
        await loadAdvertisingTargets();
        setLandingPreviewVersion((v) => v + 1);
        if (onSaved) {
          onSaved(updatedCampaign ? {
            ...updatedCampaign,
            title,
            content: contentToSave,
            adLayouts: layoutsToSave,
            landingKey: updatedCampaign.landingKey || campaign.landingKey,
            landingUrl: updatedLandingUrl,
            monetisationMode: effectiveMonetisationMode,
            monetisation_mode: effectiveMonetisationMode,
            sourceLanguageCode: translationSourceLanguageCode,
            source_language_code: translationSourceLanguageCode,
          } : {
            ...campaign,
            title,
            content: contentToSave,
            adLayouts: layoutsToSave,
            landingKey: campaign.landingKey,
            landingUrl: updatedLandingUrl,
            monetisationMode: effectiveMonetisationMode,
            monetisation_mode: effectiveMonetisationMode,
            sourceLanguageCode: translationSourceLanguageCode,
            source_language_code: translationSourceLanguageCode,
            languageVersions: normalizedLanguageVersions,
            translations: normalizedLanguageVersions.filter((row) => !sameLanguageCode(row.languageCode, translationSourceLanguageCode)),
          });
        }
        return contentToSave;
      }
      return false;
    } catch (err) {
      console.error('Failed to save campaign', err);
      alert('Failed to save campaign. Please try again.');
      return false;
    }
  };

  const handleFacebookPublish = async () => {
    if (!facebookAccountId) {
      setFacebookPublishState({ loading: false, error: 'Select a connected Facebook Page.', publication: null });
      return;
    }
    if (!window.confirm('Post this campaign to Facebook now? This publishes immediately and cannot be undone from Suitestream.')) return;
    setFacebookPublishState({ loading: true, error: '', publication: null });
    const saved = await handleSave();
    if (!saved) {
      setFacebookPublishState({ loading: false, error: 'Save the campaign before posting to Facebook.', publication: null });
      return;
    }
    try {
      const response = await fetch(`/api/social/facebook/campaigns/${campaign.id}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ siteId: site?.id, accountId: Number(facebookAccountId) }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Failed to post campaign to Facebook.');
      setData((current) => ({ ...current, facebookPublication: body.publication }));
      setFacebookPublishState({ loading: false, error: '', publication: body.publication });
    } catch (error) {
      setFacebookPublishState({ loading: false, error: error.message, publication: null });
    }
  };

  const handleDelete = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/creative/campaigns/${campaign.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok && onDeleted) onDeleted(campaign.id);
    } catch (err) {
      console.error('Failed to delete campaign', err);
    }
  };

  const canSaveAnnouncementWith = useCallback(({ titleText, text, language, voice }) => (
    Boolean(String(titleText || '').trim())
    && Boolean(String(text || '').trim())
    && Boolean(String(language || '').trim())
    && Boolean(String(voice || '').trim())
  ), []);

  const saveAnnouncement = useCallback(async ({
    titleText = title,
    text = data.announcement,
    language = announcementSettings.language,
    voice = announcementSettings.voice,
    silent = false,
  } = {}) => {
    if (!String(titleText || '').trim()) {
      if (!silent) {
        alert('Please enter a campaign title!');
      }
      return false;
    }
    if (!String(text || '').trim()) {
      if (!silent) {
        alert('Please enter some text!');
      }
      return false;
    }
    if (!String(language || '').trim() || !String(voice || '').trim()) {
      if (!silent) {
        alert('Please select language and voice!');
      }
      return false;
    }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/announcements', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: null,
          title: String(titleText || '').trim(),
          content: String(text || '').trim(),
          languageCode: String(language || '').trim(),
          voiceName: String(voice || '').trim(),
          deviceId: null,
          campaignId: campaign.id,
          variationIdx: 0,
        }),
      });
      const resp = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(resp.message || 'Failed to save announcement');
      setAnnouncementFolded(true);
      if (onAnnouncementSaved) {
        onAnnouncementSaved({
          id: resp.id,
          title: String(titleText || '').trim(),
          campaignId: campaign.id,
          variation: 0,
        });
      }
      return true;
    } catch (err) {
      console.error('Error saving announcement:', err);
      if (!silent) {
        alert('Failed to save announcement. Check console for details.');
      }
      return false;
    }
  }, [
    announcementSettings.language,
    announcementSettings.voice,
    campaign.id,
    data.announcement,
    onAnnouncementSaved,
    title,
  ]);

  const handleSaveAll = useCallback(async ({
    silentAnnouncement = true,
    persistAnnouncement = true,
  } = {}) => {
    const savedContent = await handleSave();
    if (!savedContent) return false;

    const announcementSnapshot = {
      titleText: title,
      text: data.announcement,
      language: announcementSettings.language,
      voice: announcementSettings.voice,
    };
    if (persistAnnouncement && canSaveAnnouncementWith(announcementSnapshot)) {
      await saveAnnouncement({
        ...announcementSnapshot,
        silent: silentAnnouncement,
      });
    }
    return true;
  }, [
    announcementSettings.language,
    announcementSettings.voice,
    canSaveAnnouncementWith,
    data.announcement,
    handleSave,
    saveAnnouncement,
    title,
  ]);

  useEffect(() => {
    if (!queuedAutoSaveSignal || !autoSaveReady || autoSaveInFlightRef.current) return;
    let cancelled = false;
    const timerId = setTimeout(async () => {
      autoSaveInFlightRef.current = true;
      if (!cancelled) {
        setQueuedAutoSaveSignal(0);
      }
      try {
        await handleSaveAllRef.current?.({
          silentAnnouncement: true,
          persistAnnouncement: false,
        });
      } finally {
        autoSaveInFlightRef.current = false;
      }
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [autoSaveReady, queuedAutoSaveSignal]);

  // Keep ref current so deferred auto-save uses the latest closure.
  handleSaveAllRef.current = handleSaveAll;

  const handlePreviewAnnouncement = async () => {
    const text = data.announcement;
    const { language, voice } = announcementSettings;
    if (!text.trim()) {
      alert('Please enter text to preview!');
      return;
    }
    if (!language) {
      alert('Please select a language!');
      return;
    }
    if (!voice) {
      alert('Please select a voice!');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/tts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          text,
          languageCode: language,
          voiceName: voice,
        }),
      });
      if (!res.ok) throw new Error('Failed to generate speech.');
      const blob = await res.blob();
      const audioURL = URL.createObjectURL(blob);
      new Audio(audioURL).play();
    } catch (err) {
      console.error('Error generating speech:', err);
      alert('Error generating speech. Check console for details.');
    }
  };

  const handleSaveAnnouncement = async () => {
    return saveAnnouncement();
  };

  const copyLink = async (url, successMessage, promptLabel) => {
    if (!url) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        alert(successMessage);
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch (err) {
      console.error(`Failed to copy ${promptLabel.toLowerCase()}`, err);
      if (typeof window !== 'undefined') {
        window.prompt(promptLabel, url);
      }
    }
  };

  const handleCopyLandingUrl = () => copyLink(landingUrl, 'Landing page link copied to clipboard!', 'Landing page URL');

  const handleCopyTvUrl = () => copyLink(tvUrl, 'TV display link copied to clipboard!', 'TV display URL');

  const handleSaveOverlay = async (options = {}) => {
    const { skipScroll } = options;
    if (!title.trim()) {
      alert('Please enter a campaign title!');
      return null;
    }
    const token = localStorage.getItem('token');
    const assetLinks = await fetchSignedUrlForAsset(selectedImageAsset);
    const signedImageUrl = assetLinks?.signedUrl;
    const overlayImageUrlToSave = signedImageUrl || (data.overlayImageUrl ? data.overlayImageUrl.trim() : '');
    const overlayQrCodeToSave = data.overlayQRCodeUrl ? data.overlayQRCodeUrl.trim() : '';
    const payload = {
      name: `${title} Overlay`,
      type: 'top',
      message: data.overlay,
      image_url: overlayImageUrlToSave || null,
      qr_code_url: overlayQrCodeToSave || null,
      duration: 7,
      site_id: site?.id || null,
      campaign_id: campaign.id,
    };
    try {
      if (overlayId) {
        await fetch(`/api/overlays/${overlayId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        setData((prev) => ({
          ...prev,
          overlayId,
          overlayImageUrl: overlayImageUrlToSave,
          overlayQRCodeUrl: overlayQrCodeToSave,
        }));
        if (!skipScroll) {
          setOverlayFolded(true);
        }
        if (overlayQrCodeToSave) {
          setLandingUrl(overlayQrCodeToSave);
        }
        if (onOverlaySaved) {
          onOverlaySaved({ id: overlayId, image_url: overlayImageUrlToSave, qr_code_url: overlayQrCodeToSave });
        }
        return overlayId;
      } else {
        const res = await fetch('/api/overlays', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('Failed to save overlay');
        const dataRes = await res.json();
        setOverlayId(dataRes.overlay.id);
        setData((prev) => ({
          ...prev,
          overlayId: dataRes.overlay.id,
          overlayImageUrl: overlayImageUrlToSave,
          overlayQRCodeUrl: overlayQrCodeToSave,
        }));
        if (!skipScroll) {
          setOverlayFolded(true);
        }
        if (overlayQrCodeToSave) {
          setLandingUrl(overlayQrCodeToSave);
        }
        if (onOverlaySaved) {
          onOverlaySaved({ id: dataRes.overlay.id, image_url: overlayImageUrlToSave, qr_code_url: overlayQrCodeToSave });
        }
        return dataRes.overlay.id;
      }
    } catch (err) {
      console.error('Error saving overlay:', err);
      alert('Failed to save overlay.');
      return null;
    }
  };

  const handlePreviewOverlay = async () => {
    const id = overlayId || (await handleSaveOverlay({ skipScroll: true }));
    if (!id || !previewDeviceId) return;
    try {
      await playOverlayOnPreviewDevice(id);
    } catch (err) {
      console.error('Error playing overlay:', err);
      alert('Failed to preview overlay.');
    }
  };

  const handlePreviewTvOverlay = async () => {
    if (!previewDeviceId) return;
    // Save and regenerate whenever the overlay URL is missing or stale (image_url may be null on old records)
    let id = data.tvOverlayId;
    if (!id || !data.tvOverlayUrl) {
      const saved = await handleSave();
      if (!saved) {
        alert('Please save the campaign first so the TV overlay can be generated.');
        return;
      }
      // handleSave returns contentToSave which includes the freshly-generated tvOverlayId
      id = saved.tvOverlayId || data.tvOverlayId;
    }
    if (!id) return;
    try {
      await playOverlayOnPreviewDevice(id);
    } catch (err) {
      console.error('Error playing overlay:', err);
      alert('Failed to preview TV overlay.');
    }
  };

  const filteredVoices = availableVoices.filter((voice) =>
    voice.languageCodes.includes(announcementSettings.language)
  );
  const truncateText = (value, maxLength = 120) => {
    const text = String(value || '').trim();
    if (!text) return '';
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  const panelCompletion = {
    image: imageFolded,
    tv: tvFolded,
    social: socialFolded,
    announcement: announcementFolded,
    overlay: overlayFolded,
    landing: landingFolded,
    advertising: advertisingFolded,
    translations: nonSourceCampaignLanguageVersions.length > 0,
  };
  const panelsLocked = Boolean(generationProgress?.isLocked);
  const panelStatusLookup = generationProgress?.panels || {};
  const voucherOffer = normalizeVoucherOffer(data.voucherOffer);
  const landingTheme = normalizeCampaignLandingTheme(data.landingTheme);
  const voucherClaimSummary = formatVoucherClaimSummary(voucherOffer.maxClaimsPerDevicePerCampaign);
  const voucherExpiryParts = splitVoucherExpiryHours(voucherOffer.expiresAfterHours);
  const voucherExpiryLabel = formatVoucherExpiryLabel(voucherOffer.expiresAfterHours);
  const voucherBackgroundDisplayColor = resolveEditorColor(
    voucherOffer.backgroundColor,
    DEFAULT_VOUCHER_BACKGROUND_DISPLAY_COLOR
  );
  const voucherTextDisplayColor = resolveEditorColor(
    voucherOffer.textColor,
    DEFAULT_VOUCHER_TEXT_DISPLAY_COLOR
  );
  const voucherLandingBackgroundDisplayColor = resolveEditorColor(
    voucherOffer.landingBackgroundColor,
    voucherBackgroundDisplayColor
  );
  const voucherLandingTextDisplayColor = resolveEditorColor(
    voucherOffer.landingTextColor,
    voucherTextDisplayColor
  );
  const landingThemeDisplayColors = {
    pageBackgroundColor: resolveEditorColor(
      landingTheme.pageBackgroundColor,
      DEFAULT_CAMPAIGN_THEME_DISPLAY.pageBackgroundColor
    ),
    pageBackgroundAccentColor: resolveEditorColor(
      landingTheme.pageBackgroundAccentColor,
      DEFAULT_CAMPAIGN_THEME_DISPLAY.pageBackgroundAccentColor
    ),
    panelBackgroundColor: resolveEditorColor(
      landingTheme.panelBackgroundColor,
      DEFAULT_CAMPAIGN_THEME_DISPLAY.panelBackgroundColor
    ),
    sectionBackgroundColor: resolveEditorColor(
      landingTheme.sectionBackgroundColor,
      DEFAULT_CAMPAIGN_THEME_DISPLAY.sectionBackgroundColor
    ),
    headingTextColor: resolveEditorColor(
      landingTheme.headingTextColor,
      DEFAULT_CAMPAIGN_THEME_DISPLAY.headingTextColor
    ),
    bodyTextColor: resolveEditorColor(
      landingTheme.bodyTextColor,
      DEFAULT_CAMPAIGN_THEME_DISPLAY.bodyTextColor
    ),
    accentColor: resolveEditorColor(
      landingTheme.accentColor,
      DEFAULT_CAMPAIGN_THEME_DISPLAY.accentColor
    ),
    mutedTextColor: resolveEditorColor(
      landingTheme.mutedTextColor,
      DEFAULT_CAMPAIGN_THEME_DISPLAY.mutedTextColor
    ),
  };

  const panelDescriptors = [
    {
      key: 'image',
      title: PANEL_TITLES.image,
      accent: 'Artwork and hero image',
      summary: selectedImageAsset
        ? (selectedImageAsset.name || selectedImageAsset.filename || 'Artwork selected')
        : 'No image selected yet.',
      detail: selectedImageAsset?.filename ? `File: ${selectedImageAsset.filename}` : 'Choose the artwork used across campaign outputs.',
    },
    {
      key: 'tv',
      title: PANEL_TITLES.tv,
      accent: 'Screen layout and TV preview',
      summary: tvLayout.templateName || selectedTemplateId.replace(/-/g, ' '),
      detail: tvUrl ? truncateText(tvUrl, 72) : 'TV link will appear once the campaign has been saved.',
    },
    {
      key: 'social',
      title: PANEL_TITLES.social,
      accent: 'Facebook and Google copy',
      summary: truncateText(data.facebookPost || data.googleUpdate, 96) || 'No social copy written yet.',
      detail: data.facebookPost && data.googleUpdate ? 'Facebook and Google posts are both populated.' : 'Write social copy and launch Canva sizes from here.',
    },
    {
      key: 'announcement',
      title: PANEL_TITLES.announcement,
      accent: 'Voice and playback copy',
      summary: truncateText(data.announcement, 96) || 'No announcement text yet.',
      detail: announcementSettings.language
        ? `${getLanguageLabel(announcementSettings.language)}${announcementSettings.voice ? ` • ${getVoiceDisplayName(announcementSettings.voice, announcementSettings.language)}` : ''}`
        : 'Choose the TTS language and voice.',
    },
    {
      key: 'overlay',
      title: PANEL_TITLES.overlay,
      accent: 'QR prompt and overlay media',
      summary: truncateText(data.overlay, 96) || 'No overlay message yet.',
      detail: data.overlayQRCodeUrl ? truncateText(data.overlayQRCodeUrl, 72) : 'Add image and QR destinations for overlay playback.',
    },
    {
      key: 'landing',
      title: PANEL_TITLES.landing,
      accent: 'Landing copy and related campaigns',
      summary: voucherOffer.enabled
        ? truncateText(voucherOffer.headline || voucherOffer.offerTitle, 96) || 'Voucher CTA enabled.'
        : truncateText(data.landingPage, 96) || 'No landing page copy yet.',
      detail: voucherOffer.enabled
        ? `${voucherClaimSummary} ${voucherExpiryLabel} expiry.`
        : (landingUrl ? truncateText(landingUrl, 72) : 'Landing link will appear after save and overlay generation.'),
    },
    {
      key: 'advertising',
      title: PANEL_TITLES.advertising,
      accent: 'Cross-placement distribution',
      summary: selectedAdvertisingPlacements.length
        ? `${selectedAdvertisingPlacements.length} placement${selectedAdvertisingPlacements.length === 1 ? '' : 's'} selected`
        : 'No advertising placements selected.',
      detail: advertisingPlacementCompositionChanged && nonSourceCampaignLanguageVersions.length
        ? 'Save, then review translated parent landing pages that surface this campaign.'
        : selectedAdvertisingPlacements.length
          ? truncateText(selectedAdvertisingPlacements.map((placement) => placement.targetTitle).join(', '), 96)
          : 'Choose where this campaign should appear across other landing pages.',
    },
    {
      key: 'translations',
      title: PANEL_TITLES.translations,
      accent: 'Landing and voucher guest copy',
      summary: nonSourceCampaignLanguageVersions.length
        ? `${nonSourceCampaignLanguageVersions.length} language${nonSourceCampaignLanguageVersions.length === 1 ? '' : 's'} added`
        : 'No campaign translations yet.',
      detail: campaignTranslationReviewNeeded
        ? `Review needed: ${truncateText(campaignTranslationReviewReasons.join(' • '), 96)}`
        : siteTranslationLanguages.length
          ? `Suggested from site languages: ${truncateText(siteTranslationLanguages.map((languageCode) => getLanguageLabel(languageCode)).join(', '), 96)}`
          : 'Translate only the landing title, landing body, and voucher copy.',
    },
  ];

  const openPanel = (panelKey) => {
    if (panelsLocked) return;
    if (panelKey === 'image') setImageFolded(false);
    if (panelKey === 'tv') setTvFolded(false);
    if (panelKey === 'social') setSocialFolded(false);
    if (panelKey === 'announcement') setAnnouncementFolded(false);
    if (panelKey === 'overlay') setOverlayFolded(false);
    if (panelKey === 'landing') setLandingFolded(false);
    if (panelKey === 'advertising') setAdvertisingFolded(false);
    setActivePanel(panelKey);
  };

  const closePanel = () => setActivePanel(null);

  const handleSavePanel = async (panelKey) => {
    if (panelKey === 'announcement') {
      await handleSaveAnnouncement();
      return;
    }
    if (panelKey === 'overlay') {
      await handleSaveOverlay({ skipScroll: true });
      return;
    }

    await handleSave();
  };

  const renderImageEditor = () => (
    <div className="space-y-4">
      <div className="flex flex-col xl:flex-row gap-4">
        <PhotoAssetBrowser
          assets={assets}
          siteId={site?.id}
          promptText={promptText}
          campaignTitle={title}
          selectedAssetId={selectedAssetId}
          selectedAssetIds={selectedHeroAssetIds}
          multiSelect
          onSelectAsset={handleAssetSelect}
          onToggleAsset={handleToggleHeroAsset}
          onUploadComplete={onRefreshAssets}
          className="xl:w-[360px]"
        />
        <div className="flex-1 flex flex-col gap-4">
          <SelectedAssetPreview
            asset={selectedImageAsset}
            className="bg-gray-900 text-white rounded-lg"
            maxHeight={360}
          />
          <div className="rounded border border-gray-800 bg-gray-950/60 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-white">Landing Page Hero Images</div>
                <div className="mt-1 text-xs text-gray-400">
                  Select one image for a static hero or multiple images for an optional landing-page slideshow.
                </div>
              </div>
              <div className="text-xs uppercase tracking-wide text-gray-500">
                {selectedHeroAssets.length} selected
              </div>
            </div>

            {selectedHeroAssets.length ? (
              <div className="mt-3 space-y-2">
                {selectedHeroAssets.map((asset, index) => (
                  <div
                    key={`hero-asset-${asset.id}`}
                    className="flex items-center gap-3 rounded border border-gray-800 bg-black/20 px-3 py-2"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-full border border-indigo-700/50 bg-indigo-950/40 text-xs font-semibold text-indigo-100">
                      {index + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-white">
                        {asset.name || asset.filename || `Asset #${asset.id}`}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {index === 0 ? 'Primary hero image and fallback cover.' : 'Shown in slideshow order.'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="rounded border border-gray-700 bg-gray-900 px-2 py-1 text-xs text-white disabled:opacity-40"
                        onClick={() => moveHeroAsset(asset.id, 'up')}
                        disabled={index === 0}
                      >
                        Up
                      </button>
                      <button
                        type="button"
                        className="rounded border border-gray-700 bg-gray-900 px-2 py-1 text-xs text-white disabled:opacity-40"
                        onClick={() => moveHeroAsset(asset.id, 'down')}
                        disabled={index === selectedHeroAssets.length - 1}
                      >
                        Down
                      </button>
                      <button
                        type="button"
                        className="rounded border border-gray-700 bg-gray-900 px-2 py-1 text-xs text-white"
                        onClick={() => handleAssetSelect(asset)}
                      >
                        Make cover
                      </button>
                      <button
                        type="button"
                        className="rounded border border-rose-800/60 bg-rose-950/40 px-2 py-1 text-xs text-rose-100"
                        onClick={() => handleToggleHeroAsset(asset)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-3 rounded border border-gray-800 bg-black/20 px-3 py-3 text-sm text-gray-400">
                Select at least one image from the browser on the left.
              </div>
            )}

            <div className="mt-4 rounded border border-gray-800 bg-black/20 p-3">
              <div className="text-sm font-semibold text-white">Slideshow Settings</div>
              <div className="mt-1 text-xs text-gray-400">
                Arrow controls appear automatically on the landing page when the slideshow is enabled.
              </div>
              <div className={`mt-3 grid gap-3 md:grid-cols-3 ${selectedHeroAssets.length > 1 ? '' : 'opacity-60'}`}>
                <label className="flex items-center gap-2 text-sm text-gray-200">
                  <input
                    type="checkbox"
                    checked={Boolean(data.heroSlideshow?.enabled) && selectedHeroAssets.length > 1}
                    onChange={(e) => updateHeroSlideshowField('enabled', e.target.checked)}
                    disabled={selectedHeroAssets.length <= 1}
                  />
                  Enable slideshow
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-200">
                  <input
                    type="checkbox"
                    checked={Boolean(data.heroSlideshow?.autoRotate)}
                    onChange={(e) => updateHeroSlideshowField('autoRotate', e.target.checked)}
                    disabled={selectedHeroAssets.length <= 1 || !data.heroSlideshow?.enabled}
                  />
                  Auto-rotate
                </label>
                <label className="text-sm text-gray-200">
                  <span className="mb-1 block text-xs uppercase tracking-wide text-gray-500">Interval seconds</span>
                  <input
                    type="number"
                    min="2"
                    max="30"
                    className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white"
                    value={normalizeHeroSlideshow(data.heroSlideshow).intervalSeconds}
                    onChange={(e) => updateHeroSlideshowField('intervalSeconds', normalizePositiveInteger(e.target.value, DEFAULT_HERO_SLIDESHOW_INTERVAL_SECONDS, { min: 2, max: 30 }))}
                    disabled={selectedHeroAssets.length <= 1 || !data.heroSlideshow?.enabled || !data.heroSlideshow?.autoRotate}
                  />
                </label>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <button
              className="px-3 py-2 bg-emerald-600 rounded text-white whitespace-nowrap hover:bg-emerald-500 transition"
              onClick={() => handleDesign('tvContent')}
            >
              Generate design in Canva
            </button>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button className="px-3 py-2 bg-gray-700 rounded text-white" onClick={closePanel}>
          Close
        </button>
        <button className="px-3 py-2 bg-indigo-600 rounded text-white" onClick={() => handleSavePanel('image')}>
          Save
        </button>
      </div>
    </div>
  );

  const renderTvEditor = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Templates</span>
            <span className="text-xs text-gray-400 bg-gray-800/80 px-2 py-0.5 rounded border border-gray-700">
              {tvLayout.templateName || selectedTemplateId.replace(/-/g, ' ')}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="px-3 py-1.5 bg-indigo-500/80 hover:bg-indigo-500 text-white text-xs font-semibold uppercase tracking-wide rounded border border-indigo-300 transition"
              onClick={handleSaveNewTemplate}
            >
              Save New Template
            </button>
          </div>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {TV_TEMPLATES.map((tpl) => (
            <button
              type="button"
              key={tpl.id}
              className={`min-w-[180px] px-3 py-2 rounded border transition text-left ${
                tpl.id === selectedTemplateId
                  ? 'border-indigo-400 bg-indigo-600/20 text-white'
                  : 'border-gray-700 bg-gray-800/60 text-gray-200 hover:border-indigo-400/60'
              }`}
              onClick={() => handleTemplateSelect(tpl.id)}
            >
              <div className="text-sm font-semibold mb-1">{tpl.name}</div>
              <div className="text-xs text-gray-300/80 leading-snug">{tpl.description}</div>
            </button>
          ))}
        </div>
        {userTemplates.length ? (
          <div className="space-y-2">
            <span className="text-sm font-semibold text-gray-300 uppercase tracking-wide">User Templates</span>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {userTemplates.map((tpl) => (
                <button
                  type="button"
                  key={tpl.id}
                  className={`min-w-[160px] px-3 py-2 rounded border transition text-left ${
                    tpl.id === selectedTemplateId
                      ? 'border-indigo-400 bg-indigo-600/20 text-white'
                      : 'border-gray-700 bg-gray-800/60 text-gray-200 hover:border-indigo-400/60'
                  }`}
                  onClick={() => handleTemplateSelect(tpl.id)}
                >
                  <div className="text-sm font-semibold">{tpl.name}</div>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
      <TVLayoutEditor
        layout={tvLayout}
        tvContent={data.tvContent}
        imageUrl={tvPreviewImageUrl}
        landingUrl={landingUrl || tvUrl}
        onChange={(nextLayout) => updateTvLayout(() => nextLayout)}
        onContentChange={(binding, value) => updateTv(binding, value)}
      />
      {tvUrl && (
        <div className="flex flex-wrap items-center gap-2 bg-gray-800/70 p-2 rounded border border-gray-700">
          <span className="text-xs sm:text-sm break-all flex-1">{tvUrl}</span>
          <div className="flex flex-wrap gap-2 justify-end">
            <button
              className="px-2 py-1 bg-indigo-600 rounded text-white"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.open(tvUrl, '_blank', 'noopener');
                }
              }}
            >
              Open
            </button>
            <button className="px-2 py-1 bg-gray-600 rounded text-white" onClick={handleCopyTvUrl}>
              Copy
            </button>
            <button
              className={`px-2 py-1 rounded text-white ${
                hasDevices && previewDeviceId ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-gray-700 cursor-not-allowed'
              }`}
              onClick={handlePreviewTvOverlay}
              disabled={!hasDevices || !previewDeviceId}
            >
              Preview on Selected TV
            </button>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button className="px-3 py-2 bg-gray-700 rounded text-white" onClick={closePanel}>
          Close
        </button>
        <button className="px-3 py-2 bg-indigo-600 rounded text-white" onClick={() => handleSavePanel('tv')}>
          Save
        </button>
      </div>
    </div>
  );

  const renderSocialEditor = () => (
    <div className="space-y-4">
      <div className="flex flex-col xl:flex-row gap-4">
        <div className="bg-transparent rounded-lg flex-1">
          <h4 className="text-lg font-semibold text-white mb-3">Facebook Post</h4>
          <textarea
            className="w-full mb-2 p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={data.facebookPost}
            onChange={(e) => updateField('facebookPost', e.target.value)}
            rows={6}
          />
          <div className="mb-3 overflow-hidden rounded-xl border border-gray-700 bg-white text-gray-900 shadow-lg">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 font-bold text-white">
                {(facebookConnection?.accounts?.find((account) => String(account.id) === String(facebookAccountId))?.account_name || 'F').slice(0, 1).toUpperCase()}
              </div>
              <div>
                <div className="font-semibold">
                  {facebookConnection?.accounts?.find((account) => String(account.id) === String(facebookAccountId))?.account_name || 'Facebook Page preview'}
                </div>
                <div className="text-xs text-gray-500">Just now · Public</div>
              </div>
            </div>
            <div className="whitespace-pre-wrap px-4 pb-3 text-sm">{data.facebookPost || 'Your Facebook copy will appear here.'}</div>
            {selectedHeroAssets.length > 0 && (
              <div className={`grid bg-gray-100 ${selectedHeroAssets.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {selectedHeroAssets.slice(0, 4).map((asset, index) => (
                  <div key={asset.id} className="relative aspect-square overflow-hidden border border-white/70">
                    <img
                      src={asset.preview_url || asset.thumbnail_url || asset.preview_api_url || asset.thumbnail_api_url || asset.url}
                      alt={`Facebook artwork ${index + 1}`}
                      className="h-full w-full object-cover"
                    />
                    {index === 3 && selectedHeroAssets.length > 4 && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-3xl font-semibold text-white">
                        +{selectedHeroAssets.length - 4}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-around border-t border-gray-200 px-3 py-2 text-sm font-medium text-gray-500">
              <span>Like</span><span>Comment</span><span>Share</span>
            </div>
          </div>
          {facebookConnection?.publishingEnabled && (
            <div className="rounded-lg border border-blue-900/60 bg-blue-950/20 p-3 space-y-3">
              <div className="text-sm font-semibold text-blue-100">Publish to Facebook</div>
              {facebookConnection?.accounts?.some((account) => account.enabled) ? (
                <select
                  className="w-full rounded border border-gray-700 bg-gray-800 p-2 text-white"
                  value={facebookAccountId}
                  onChange={(event) => setFacebookAccountId(event.target.value)}
                >
                  {facebookConnection.accounts.filter((account) => account.enabled).map((account) => (
                    <option key={account.id} value={account.id}>{account.account_name}</option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-gray-400">Connect and select a Facebook Page in Settings before publishing.</p>
              )}
              {facebookConnection && !facebookConnection.publishingAuthorized && (
                <p className="text-sm text-amber-300">
                  Publishing permission is not present. <a className="underline" href={`/control-panel?siteId=${site?.id || ''}&tab=Settings&settingsSubTab=Integrations`}>Reconnect Facebook</a> to grant it.
                </p>
              )}
              {facebookPublishState.error && <p className="text-sm text-red-300">{facebookPublishState.error}</p>}
              {(facebookPublishState.publication || data.facebookPublication) && (
                <p className="text-sm text-emerald-300">
                  Posted to {(facebookPublishState.publication || data.facebookPublication).accountName}.{' '}
                  <a className="underline" href={(facebookPublishState.publication || data.facebookPublication).permalink} target="_blank" rel="noreferrer">View post</a>
                </p>
              )}
              <button
                type="button"
                className="w-full rounded bg-blue-600 px-3 py-2 font-semibold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-gray-700"
                onClick={handleFacebookPublish}
                disabled={facebookPublishState.loading || !facebookAccountId || !facebookConnection?.publishingAuthorized}
              >
                {facebookPublishState.loading ? 'Posting…' : 'Post Now'}
              </button>
              <p className="text-xs text-gray-400">Post Now publishes the saved copy and all selected campaign artwork immediately.</p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-3">
            <select
              className="p-2 bg-gray-800 rounded text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-auto"
              value={sizes.fb}
              onChange={(e) => handleSizeChange('fb', e.target.value)}
            >
              {FB_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            <button
              className="px-3 py-2 bg-emerald-600 rounded text-white whitespace-nowrap hover:bg-emerald-500 transition"
              onClick={() => handleDesign('facebookPost')}
            >
              Generate design in Canva
            </button>
          </div>
        </div>
        <div className="bg-transparent rounded-lg flex-1">
          <h4 className="text-lg font-semibold text-white mb-3">Google Update</h4>
          <textarea
            className="w-full mb-2 p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={data.googleUpdate}
            onChange={(e) => updateField('googleUpdate', e.target.value)}
            rows={6}
          />
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-3">
            <select
              className="p-2 bg-gray-800 rounded text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-auto"
              value={sizes.gmb}
              onChange={(e) => handleSizeChange('gmb', e.target.value)}
            >
              {GMB_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            <button
              className="px-3 py-2 bg-emerald-600 rounded text-white whitespace-nowrap hover:bg-emerald-500 transition"
              onClick={() => handleDesign('googleUpdate')}
            >
              Generate design in Canva
            </button>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button className="px-3 py-2 bg-gray-700 rounded text-white" onClick={closePanel}>
          Close
        </button>
        <button className="px-3 py-2 bg-indigo-600 rounded text-white" onClick={() => handleSavePanel('social')}>
          Save
        </button>
      </div>
    </div>
  );

  const renderAnnouncementEditor = () => (
    <div className="space-y-4">
      <textarea
        className="w-full mb-2 p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        value={data.announcement}
        onChange={(e) => updateField('announcement', e.target.value)}
        rows={10}
      />
      <div>
        <label className="block text-sm mb-1">Language</label>
        <select
          className="p-2 bg-gray-800 rounded text-white border border-gray-700 w-full mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          value={announcementSettings.language}
          onChange={(e) => updateAnnouncementSetting('language', e.target.value)}
        >
          <option value="">-- Select a Language --</option>
          {uniqueLanguages.map((lang) => (
            <option key={lang} value={lang}>
              {getLanguageLabel(lang)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm mb-1">Voice</label>
        <select
          className="p-2 bg-gray-800 rounded text-white border border-gray-700 w-full mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          value={announcementSettings.voice}
          onChange={(e) => updateAnnouncementSetting('voice', e.target.value)}
          disabled={!announcementSettings.language}
        >
          <option value="">-- Select a Voice --</option>
          {filteredVoices.map((voice) => (
            <option key={voice.name} value={voice.name}>
              {getVoiceDisplayName(voice.name, announcementSettings.language)}
            </option>
          ))}
        </select>
      </div>
      <div className="flex justify-end gap-2">
        <button className="px-3 py-2 bg-gray-600 rounded text-white" onClick={handlePreviewAnnouncement}>
          Preview
        </button>
        <button className="px-3 py-2 bg-gray-700 rounded text-white" onClick={closePanel}>
          Close
        </button>
        <button className="px-3 py-2 bg-indigo-600 rounded text-white" onClick={() => handleSavePanel('announcement')}>
          Save
        </button>
      </div>
    </div>
  );

  const renderOverlayEditor = () => (
    <div className="space-y-4">
      <textarea
        className="w-full mb-2 p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        value={data.overlay}
        onChange={(e) => updateField('overlay', e.target.value)}
        rows={3}
      />
      <div>
        <label className="block text-sm mb-1">Image URL</label>
        <input
          type="text"
          className="w-full mb-2 p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          value={data.overlayImageUrl}
          onChange={(e) => updateField('overlayImageUrl', e.target.value)}
        />
      </div>
      <div>
        <label className="block text-sm mb-1">QR Code URL</label>
        <input
          type="text"
          className="w-full mb-2 p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          value={data.overlayQRCodeUrl}
          onChange={(e) => updateField('overlayQRCodeUrl', e.target.value)}
        />
      </div>
      <div className="flex justify-end gap-2">
        {hasDevices && (
          <button className="px-3 py-2 bg-gray-600 rounded text-white" onClick={handlePreviewOverlay}>
            Preview
          </button>
        )}
        <button className="px-3 py-2 bg-gray-700 rounded text-white" onClick={closePanel}>
          Close
        </button>
        <button className="px-3 py-2 bg-indigo-600 rounded text-white" onClick={() => handleSavePanel('overlay')}>
          Save
        </button>
      </div>
    </div>
  );

  const renderLandingEditor = () => (
    <div className="space-y-4">
      {data.socialSource?.provider === 'facebook' && (
        <div className="rounded border border-blue-700/50 bg-blue-950/25 p-4">
          <div className="text-sm font-semibold text-white">QR destination</div>
          <div className="mt-1 text-xs text-blue-100/80">
            Choose whether campaign QR codes open the original Facebook post or this regular SoundScreen landing page.
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <label className={`cursor-pointer rounded border p-3 ${data.socialSource.qrDestination === 'original_post' ? 'border-blue-400 bg-blue-600/20' : 'border-gray-700 bg-gray-900/50'}`}>
              <input
                type="radio"
                name="social-qr-destination"
                className="mr-2"
                checked={data.socialSource.qrDestination === 'original_post'}
                onChange={() => updateSocialQrDestination('original_post')}
              />
              <span className="font-medium text-white">Original Facebook post</span>
              <span className="mt-1 block text-xs text-gray-300">Preserves the direct social redirect.</span>
            </label>
            <label className={`cursor-pointer rounded border p-3 ${data.socialSource.qrDestination === 'landing_page' ? 'border-emerald-400 bg-emerald-600/20' : 'border-gray-700 bg-gray-900/50'}`}>
              <input
                type="radio"
                name="social-qr-destination"
                className="mr-2"
                checked={data.socialSource.qrDestination === 'landing_page'}
                onChange={() => updateSocialQrDestination('landing_page')}
              />
              <span className="font-medium text-white">SoundScreen landing page</span>
              <span className="mt-1 block text-xs text-gray-300">Uses the generated campaign page and its fresh URL.</span>
            </label>
          </div>
        </div>
      )}
      {landingUrl && (
        <div className="flex flex-wrap items-center gap-2 bg-gray-800 p-2 rounded border border-gray-700">
          <span className="text-xs sm:text-sm break-all flex-1">{landingUrl}</span>
          <div className="flex flex-wrap gap-2 justify-end">
            <button
              className="px-2 py-1 bg-indigo-600 rounded text-white"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.open(landingUrl, '_blank', 'noopener');
                }
              }}
            >
              Open
            </button>
            <button className="px-2 py-1 bg-gray-600 rounded text-white" onClick={handleCopyLandingUrl}>
              Copy
            </button>
          </div>
        </div>
      )}
      {campaignTranslationReviewNeeded && (
        <div className="rounded border border-amber-700/60 bg-amber-950/30 px-3 py-3 text-sm text-amber-100">
          <div className="font-semibold text-amber-50">Translation review needed</div>
          <div className="mt-1">
            {campaignTranslationReviewReasons.join(' • ')}.
          </div>
          <div className="mt-1 text-xs text-amber-100/80">
            Save, then review the campaign’s translated landing and voucher rows in the `Translations` panel.
          </div>
        </div>
      )}
      <div className="rounded border border-indigo-900/40 bg-indigo-950/10">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
          onClick={() => setLandingThemeOpen((prev) => !prev)}
        >
          <div>
            <div className="text-sm font-semibold text-white">Landing Page Theme</div>
            <div className="text-xs text-gray-300">
              Theme the page background, main panel, supporting panels, headings, body copy, and links.
            </div>
          </div>
          <div className="text-xs text-indigo-100">
            {landingThemeOpen ? 'Hide' : 'Show'}
          </div>
        </button>
        {landingThemeOpen && (
          <div className="border-t border-indigo-900/30 p-4">
            <div className="mb-4 text-xs text-gray-400">
              These colours drive the campaign landing page outside the voucher CTA. Voucher image and voucher CTA colours are set separately below.
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <VoucherColorField
                label="Page Background"
                value={landingThemeDisplayColors.pageBackgroundColor}
                onChange={(value) => updateLandingThemeField('pageBackgroundColor', value)}
              />
              <VoucherColorField
                label="Page Background Accent"
                value={landingThemeDisplayColors.pageBackgroundAccentColor}
                onChange={(value) => updateLandingThemeField('pageBackgroundAccentColor', value)}
              />
              <VoucherColorField
                label="Main Panel Background"
                value={landingThemeDisplayColors.panelBackgroundColor}
                onChange={(value) => updateLandingThemeField('panelBackgroundColor', value)}
              />
              <VoucherColorField
                label="Supporting Panel Background"
                value={landingThemeDisplayColors.sectionBackgroundColor}
                onChange={(value) => updateLandingThemeField('sectionBackgroundColor', value)}
              />
              <VoucherColorField
                label="Heading Text"
                value={landingThemeDisplayColors.headingTextColor}
                onChange={(value) => updateLandingThemeField('headingTextColor', value)}
              />
              <VoucherColorField
                label="Body Text"
                value={landingThemeDisplayColors.bodyTextColor}
                onChange={(value) => updateLandingThemeField('bodyTextColor', value)}
              />
              <VoucherColorField
                label="Accent / Link Colour"
                value={landingThemeDisplayColors.accentColor}
                onChange={(value) => updateLandingThemeField('accentColor', value)}
              />
              <VoucherColorField
                label="Muted Text"
                value={landingThemeDisplayColors.mutedTextColor}
                onChange={(value) => updateLandingThemeField('mutedTextColor', value)}
              />
            </div>
          </div>
        )}
      </div>
      <div className="rounded border border-emerald-900/60 bg-emerald-950/20">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
          onClick={() => setVoucherSettingsOpen(!voucherSettingsOpen)}
        >
          <div>
            <div className="text-sm font-semibold text-white">Voucher Offer CTA</div>
            <div className="text-xs text-gray-300">
              {voucherOffer.enabled
                ? `${voucherClaimSummary} ${voucherExpiryLabel} expiry.`
                : 'Add a download-first voucher flow to this landing page.'}
            </div>
          </div>
          <div className="text-xs text-emerald-100">
            {voucherOffer.enabled ? 'Enabled' : 'Disabled'} • {voucherSettingsOpen ? 'Hide' : 'Show'}
          </div>
        </button>
        {voucherSettingsOpen && (
          <div className="border-t border-emerald-900/40 p-4 space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-3">
                <div className="text-xs text-gray-300">
                  Guests open the voucher on their phone, then save or share it. Voucher colours drive the generated voucher image, and CTA colours can optionally override the landing-page theme above.
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    className="rounded border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-100 transition hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={handleResetVoucherUsageBlocks}
                    disabled={voucherUsageResetting || !campaign?.id}
                  >
                    {voucherUsageResetting ? 'Resetting…' : 'Reset usage blocks'}
                  </button>
                  <div className="text-[11px] text-gray-400">
                    Voids current voucher claims for this campaign so the same phones can claim again.
                  </div>
                </div>
                {voucherUsageResetMessage ? (
                  <div className="rounded border border-emerald-900/50 bg-emerald-950/30 px-3 py-2 text-xs text-emerald-100">
                    {voucherUsageResetMessage}
                  </div>
                ) : null}
                {voucherUsageResetError ? (
                  <div className="rounded border border-rose-900/50 bg-rose-950/30 px-3 py-2 text-xs text-rose-100">
                    {voucherUsageResetError}
                  </div>
                ) : null}
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-emerald-100">
                <input
                  type="checkbox"
                  checked={voucherOffer.enabled}
                  onChange={(e) => updateVoucherOfferField('enabled', e.target.checked)}
                />
                Enable voucher CTA
              </label>
            </div>

            <div className={`grid gap-4 md:grid-cols-2 ${voucherOffer.enabled ? '' : 'opacity-60'}`}>
              <div>
                <label className="block text-sm mb-1">CTA Headline</label>
                <input
                  type="text"
                  className="w-full p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={voucherOffer.headline}
                  onChange={(e) => updateVoucherOfferField('headline', e.target.value)}
                  placeholder="Claim your welcome drink voucher"
                  disabled={!voucherOffer.enabled}
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Submit Button Label</label>
                <input
                  type="text"
                  className="w-full p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={voucherOffer.submitLabel}
                  onChange={(e) => updateVoucherOfferField('submitLabel', e.target.value)}
                  placeholder="Get voucher on this phone"
                  disabled={!voucherOffer.enabled}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm mb-1">CTA Body</label>
                <textarea
                  className="w-full p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={voucherOffer.body}
                  onChange={(e) => updateVoucherOfferField('body', e.target.value)}
                  placeholder="Open your voucher on this phone, then save it, screenshot it, or share it."
                  rows={3}
                  disabled={!voucherOffer.enabled}
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Offer Title</label>
                <input
                  type="text"
                  className="w-full p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={voucherOffer.offerTitle}
                  onChange={(e) => updateVoucherOfferField('offerTitle', e.target.value)}
                  placeholder="Free welcome drink"
                  disabled={!voucherOffer.enabled}
                />
              </div>
              <VoucherColorField
                label="Voucher Background Colour"
                value={voucherBackgroundDisplayColor}
                onChange={(value) => updateVoucherOfferField('backgroundColor', value)}
                disabled={!voucherOffer.enabled}
              />
              <VoucherColorField
                label="Voucher Text Colour"
                value={voucherTextDisplayColor}
                onChange={(value) => updateVoucherOfferField('textColor', value)}
                disabled={!voucherOffer.enabled}
              />
              <VoucherColorField
                label="CTA Panel Background Colour"
                value={voucherLandingBackgroundDisplayColor}
                onChange={(value) => updateVoucherOfferField('landingBackgroundColor', value)}
                disabled={!voucherOffer.enabled}
              />
              <VoucherColorField
                label="CTA Panel Text Colour"
                value={voucherLandingTextDisplayColor}
                onChange={(value) => updateVoucherOfferField('landingTextColor', value)}
                disabled={!voucherOffer.enabled}
              />
              <div className="md:col-span-2 text-xs text-gray-400">
                Leave these unchanged to keep the landing-page voucher CTA matched to the voucher colours above.
              </div>
              <div className="md:col-span-2 rounded border border-gray-700 bg-gray-900/40 p-3">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-sm text-white">
                    <input
                      type="checkbox"
                      checked={voucherOffer.startsAfterEnabled}
                      onChange={(e) => updateVoucherOfferField('startsAfterEnabled', e.target.checked)}
                      disabled={!voucherOffer.enabled}
                    />
                    Voucher starts in
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={MAX_VOUCHER_EXPIRY_HOURS}
                    className="w-24 p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    value={voucherOffer.startsAfterHours}
                    onChange={(e) => updateVoucherOfferField('startsAfterHours', e.target.value)}
                    disabled={!voucherOffer.enabled || !voucherOffer.startsAfterEnabled}
                  />
                  <span className="text-sm text-gray-300">hours</span>
                </div>
                <div className="mt-2 text-xs text-gray-400">
                  Prevent redemption until this many hours after the voucher is issued. Disabled by default.
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm mb-1">Expires After</label>
                <div className="grid gap-3 md:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Months</label>
                    <input
                      type="number"
                      min="0"
                      max="12"
                      className="w-full p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      value={voucherExpiryParts.months}
                      onChange={(e) => updateVoucherExpiryPart('months', e.target.value)}
                      disabled={!voucherOffer.enabled}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Days</label>
                    <input
                      type="number"
                      min="0"
                      max={DAYS_PER_MONTH - 1}
                      className="w-full p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      value={voucherExpiryParts.days}
                      onChange={(e) => updateVoucherExpiryPart('days', e.target.value)}
                      disabled={!voucherOffer.enabled}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Hours</label>
                    <input
                      type="number"
                      min="0"
                      max={HOURS_PER_DAY - 1}
                      className="w-full p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      value={voucherExpiryParts.hours}
                      onChange={(e) => updateVoucherExpiryPart('hours', e.target.value)}
                      disabled={!voucherOffer.enabled}
                    />
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-400">
                  Stored internally as total hours. One month is treated as {DAYS_PER_MONTH} days.
                </div>
              </div>
              <div>
                <label className="block text-sm mb-1">Max Claims Per Device</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  className="w-full p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={voucherOffer.maxClaimsPerDevicePerCampaign}
                  onChange={(e) => updateVoucherOfferField('maxClaimsPerDevicePerCampaign', e.target.value)}
                  disabled={!voucherOffer.enabled}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm mb-1">Voucher Terms</label>
                <textarea
                  className="w-full p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={voucherOffer.terms}
                  onChange={(e) => updateVoucherOfferField('terms', e.target.value)}
                  placeholder="One per guest. Valid today only."
                  rows={3}
                  disabled={!voucherOffer.enabled}
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Terms Font Size</label>
                <input
                  type="number"
                  min="12"
                  max="24"
                  className="w-full p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={voucherOffer.termsFontSize}
                  onChange={(e) => updateVoucherOfferField('termsFontSize', e.target.value)}
                  disabled={!voucherOffer.enabled}
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Terms Style</label>
                <select
                  className="w-full p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={voucherOffer.termsStyle}
                  onChange={(e) => updateVoucherOfferField('termsStyle', e.target.value)}
                  disabled={!voucherOffer.enabled}
                >
                  <option value="normal">Normal</option>
                  <option value="italic">Italic</option>
                  <option value="bold">Bold</option>
                </select>
              </div>
            </div>

            <div className="rounded border border-emerald-900/40 bg-black/20 p-3 text-xs text-gray-300 space-y-1">
              <div><span className="font-semibold text-white">Duplicate claim rule:</span> {voucherClaimSummary}</div>
              <div><span className="font-semibold text-white">Expiry rule:</span> Voucher expires {voucherExpiryLabel} after issue.</div>
              <div><span className="font-semibold text-white">Start rule:</span> {voucherOffer.startsAfterEnabled ? `Voucher can be redeemed ${voucherOffer.startsAfterHours} hours after issue.` : 'Voucher can be redeemed immediately.'}</div>
              <div><span className="font-semibold text-white">Phase 1 redemption:</span> QR code on the voucher opens a public confirm page and staff redeem it with one `YES` action.</div>
            </div>
          </div>
        )}
      </div>
      <div className="rounded border border-gray-800 bg-black/10">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
          onClick={() => setRelatedCampaignsOpen((prev) => !prev)}
        >
          <div>
            <div className="text-sm font-semibold text-white">Add Related Campaigns</div>
            <div className="text-xs text-gray-400">
              Show links to other campaigns directly below the landing-page text.
            </div>
          </div>
          <div className="text-xs text-gray-400">
            {relatedCampaignsOpen ? 'Hide' : 'Show'}
          </div>
        </button>

        {relatedCampaignsOpen && (
          <div className="border-t border-gray-800 px-4 py-4">
            <div className="mb-4 flex flex-wrap gap-3">
              <input
                className="flex-1 min-w-[220px] rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                placeholder="Search campaigns"
                value={campaignAdsFilter.q}
                onChange={(e) => setCampaignAdsFilter({ q: e.target.value })}
              />
              <button
                className="px-3 py-2 rounded bg-gray-800 text-white text-sm disabled:bg-gray-900 disabled:text-gray-500"
                onClick={loadCampaignAds}
                disabled={campaignAdsLoading}
              >
                {campaignAdsLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            {campaignAdsError && (
              <div className="mb-3 text-sm text-red-300">{campaignAdsError}</div>
            )}

            {campaignAdsLoading && (
              <div className="mb-3 text-sm text-gray-400">Loading campaigns…</div>
            )}

            {!campaignAdsLoading && filteredRelatedCampaignAds.length === 0 && (
              <div className="mb-3 text-sm text-gray-400">
                {availableRelatedCampaignAds.length === 0
                  ? 'No other campaigns are available in this account yet.'
                  : 'No campaigns match this search.'}
              </div>
            )}

            <div className="space-y-3 max-h-[360px] overflow-auto pr-1">
              {filteredRelatedCampaignAds.map((campaignAd) => {
                const isSelected = relatedCampaignIds.some((id) => String(id) === String(campaignAd.campaignId));
                return (
                  <div
                    key={campaignAd.campaignId}
                    role="button"
                    tabIndex={0}
                    className={`rounded border px-3 py-3 ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-950/30'
                        : 'border-gray-800 bg-gray-950/60 hover:bg-gray-900'
                    }`}
                    onClick={() => toggleRelatedCampaignSelection(campaignAd.campaignId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleRelatedCampaignSelection(campaignAd.campaignId);
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-16 w-16 flex-none overflow-hidden rounded border border-gray-800 bg-gray-900">
                        {campaignAd.previewImageUrl ? (
                          <img
                            src={campaignAd.previewImageUrl}
                            alt={campaignAd.title || 'Campaign preview'}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center px-2 text-center text-[10px] text-gray-500">
                            No art
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-white">
                          {campaignAd.title || `Campaign #${campaignAd.campaignId}`}
                        </div>
                        <div className="truncate text-xs text-gray-400">
                          {campaignAd.siteName || 'Unknown site'}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {campaignAd.landingKey ? `/lp/c/${campaignAd.landingKey}` : 'No landing page'}
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-xs text-indigo-300" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={isSelected} readOnly />
                        {isSelected ? 'Selected' : 'Select'}
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 rounded border border-gray-800 bg-gray-950/60 p-3">
              <div className="text-sm font-semibold text-white">Selection Preview</div>
              <div className="mt-1 text-xs text-gray-400">
                {selectedRelatedCampaignAds.length
                  ? `${selectedRelatedCampaignAds.length} related campaign link${selectedRelatedCampaignAds.length === 1 ? '' : 's'} selected.`
                  : 'No related campaigns selected.'}
              </div>
              {relatedCampaignCompositionChanged && nonSourceCampaignLanguageVersions.length > 0 && (
                <div className="mt-3 rounded border border-amber-700/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-100">
                  Related promotions changed while this campaign already has translations. Save, then review each translated landing so the `Other Promotions` block links into translated campaign pages where available. Linked campaigns still own their own translated copy.
                </div>
              )}
              {!!selectedRelatedCampaignAds.length && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedRelatedCampaignAds.map((campaignAd) => (
                    <div
                      key={`selected-related-${campaignAd.campaignId}`}
                      className="rounded-full border border-gray-700 bg-gray-900 px-3 py-1 text-xs text-gray-200"
                    >
                      {campaignAd.title || `Campaign #${campaignAd.campaignId}`}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <textarea
            className="w-full mb-2 p-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={data.landingPage}
            onChange={(e) => updateField('landingPage', e.target.value)}
            rows={15}
          />
        </div>
        <div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col lg:items-end">
          <h4 className="text-sm font-semibold text-gray-200 mb-2">Preview</h4>
          {landingUrl ? (
            <div className="w-full max-w-[420px] border border-gray-800 rounded-lg overflow-hidden bg-black">
              <iframe
                key={`${landingUrl}-${landingPreviewVersion}`}
                src={landingPreviewSrc}
                title="Landing page preview"
                className="w-full h-[640px] bg-white"
                loading="lazy"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              />
            </div>
          ) : (
            <div className="text-sm text-gray-400">
              Landing page preview will appear once a link is generated.
            </div>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button className="px-3 py-2 bg-gray-700 rounded text-white" onClick={closePanel}>
          Close
        </button>
        <button className="px-3 py-2 bg-indigo-600 rounded text-white" onClick={() => handleSavePanel('landing')}>
          Save
        </button>
      </div>
    </div>
  );

  const renderTranslationsEditor = () => (
    <div className="space-y-4">
      <div className="rounded border border-cyan-900/40 bg-cyan-950/20 p-3 text-sm text-gray-300">
        This panel inherits languages already active on this site across menus, plaques, catalogs, and existing campaigns. It only manages the customer-facing landing title, landing body, and voucher copy.
      </div>
      {campaignTranslationReviewNeeded && (
        <div className="rounded border border-amber-700/60 bg-amber-950/30 px-3 py-3 text-sm text-amber-100">
          <div className="font-semibold text-amber-50">Review translated rows after save</div>
          <div className="mt-1">
            {campaignTranslationReviewReasons.join(' • ')}.
          </div>
          <div className="mt-1 text-xs text-amber-100/80">
            Saving will mark existing translated rows stale where needed. Open each language here afterwards and refresh or edit the guest-facing copy.
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1.6fr)]">
        <div className="space-y-4">
          <div className="rounded border border-gray-800 bg-black/20 p-3">
            <label className="block text-sm font-semibold text-white mb-2">Source Language</label>
            <select
              className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              value={translationSourceLanguageCode}
              onChange={(e) => handleTranslationSourceLanguageChange(e.target.value)}
            >
              {availableManualTranslationLanguages
                .concat(CONTENT_TRANSLATION_LANGUAGE_OPTIONS.filter((option) => sameLanguageCode(option.code, translationSourceLanguageCode)))
                .sort((left, right) => left.label.localeCompare(right.label))
                .filter((option, index, all) => all.findIndex((candidate) => candidate.code === option.code) === index)
                .map((option) => (
                  <option key={option.code} value={option.code}>{option.label}</option>
                ))}
            </select>
            <div className="mt-2 text-xs text-gray-400">
              This should match the language you are writing in inside the main landing and voucher fields.
            </div>
          </div>

          <div className="rounded border border-gray-800 bg-black/20 p-3 space-y-3">
            <div>
              <div className="text-sm font-semibold text-white">Suggested Site Languages</div>
              <div className="mt-1 text-xs text-gray-400">
                Click any language to add it to this campaign.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {siteTranslationLanguages.length ? siteTranslationLanguages.map((languageCode) => {
                const isAdded = nonSourceCampaignLanguageVersions.some((row) => sameLanguageCode(row.languageCode, languageCode));
                return (
                  <button
                    key={`site-translation-${languageCode}`}
                    type="button"
                    onClick={() => addTranslationLanguage(languageCode)}
                    className={`rounded-full border px-3 py-1 text-xs ${
                      isAdded
                        ? 'border-cyan-600 bg-cyan-950/50 text-cyan-100'
                        : 'border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800'
                    }`}
                  >
                    {getLanguageLabel(languageCode)}
                  </button>
                );
              }) : (
                <div className="text-xs text-gray-500">No site translation languages detected yet.</div>
              )}
            </div>
            <div className="flex gap-2">
              <select
                className="flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white"
                value={manualTranslationLanguageCode}
                onChange={(e) => setManualTranslationLanguageCode(e.target.value)}
              >
                {availableManualTranslationLanguages.map((option) => (
                  <option key={option.code} value={option.code}>{option.label}</option>
                ))}
              </select>
              <button
                type="button"
                className="rounded bg-gray-700 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-600"
                onClick={() => addTranslationLanguage(manualTranslationLanguageCode)}
              >
                Add
              </button>
              <button
                type="button"
                className="rounded bg-cyan-700 px-3 py-2 text-sm font-semibold text-white hover:bg-cyan-600"
                onClick={() => addTranslationLanguage(manualTranslationLanguageCode, { copySource: true })}
              >
                Copy Source
              </button>
            </div>
          </div>

          <div className="rounded border border-gray-800 bg-black/20 p-3">
            <div className="text-sm font-semibold text-white">Languages In This Campaign</div>
            <div className="mt-3 space-y-2">
              {campaignLanguageVersions.map((row) => (
                <button
                  key={`campaign-language-${row.languageCode}`}
                  type="button"
                  onClick={() => setSelectedTranslationLanguageCode(row.languageCode)}
                  className={`w-full rounded border px-3 py-3 text-left ${
                    sameLanguageCode(selectedTranslationLanguageCode, row.languageCode)
                      ? 'border-cyan-600 bg-cyan-950/30'
                      : 'border-gray-800 bg-gray-950/60 hover:bg-gray-900'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-white">{getLanguageLabel(row.languageCode)}</div>
                      <div className="mt-1 flex flex-wrap gap-2">
                        <span className={`rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide ${getTranslationFreshnessClasses(row.freshness)}`}>
                          {row.freshness || (row.isSource ? 'source' : 'current')}
                        </span>
                        {!row.isSource && (
                          <span className={`rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide ${getTranslationReviewClasses(row.reviewStatus)}`}>
                            {row.reviewStatus || 'draft'}
                          </span>
                        )}
                      </div>
                    </div>
                    {!row.isSource && (
                      <button
                        type="button"
                        className="rounded bg-gray-800 px-2 py-1 text-[11px] text-gray-200 hover:bg-gray-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeTranslationLanguage(row.languageCode);
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="mt-2 text-xs text-gray-400">
                    {row.isSource
                      ? 'Source content is edited in the main landing fields.'
                      : row.contentUpdatedAt
                        ? `Last saved ${formatTimestamp(row.contentUpdatedAt)}`
                        : 'Not saved yet.'}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded border border-gray-800 bg-black/20 p-4 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-white">
                {getLanguageLabel(selectedTranslationLanguageCode)}
              </div>
              <div className="mt-1 text-xs text-gray-400">
                {selectedTranslationVersion?.isSource
                  ? 'Source content is controlled by the landing editor.'
                  : 'Edit the translated guest-facing campaign copy here.'}
              </div>
            </div>
            {!selectedTranslationVersion?.isSource && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded bg-gray-700 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-600"
                  onClick={copySourceIntoSelectedTranslation}
                >
                  Copy Source
                </button>
                <button
                  type="button"
                  className="rounded bg-cyan-700 px-3 py-2 text-sm font-semibold text-white hover:bg-cyan-600 disabled:bg-cyan-950 disabled:text-cyan-300"
                  onClick={() => handleTranslateLanguage(selectedTranslationLanguageCode)}
                  disabled={translatingLanguageCode === selectedTranslationLanguageCode}
                >
                  {translatingLanguageCode === selectedTranslationLanguageCode ? 'Translating…' : 'Translate Now'}
                </button>
              </div>
            )}
          </div>

          {translationMessage ? (
            <div className="rounded border border-emerald-900/60 bg-emerald-950/30 px-3 py-2 text-sm text-emerald-100">
              {translationMessage}
              {translationMeta?.provider ? (
                <span className="ml-2 text-xs text-emerald-200/80">
                  {translationMeta.provider}{translationMeta.model ? ` • ${translationMeta.model}` : ''}
                </span>
              ) : null}
            </div>
          ) : null}
          {translationError ? (
            <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-sm text-red-100">
              {translationError}
            </div>
          ) : null}

          {selectedTranslationVersion?.isSource ? (
            <div className="rounded border border-sky-900/50 bg-sky-950/20 p-4 text-sm text-sky-100">
              Edit the source title, landing body, and voucher content in the main fields. This row stays visible here so the language set remains clear while you manage the translated versions.
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-1">Campaign Title</label>
                <input
                  type="text"
                  className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
                  value={selectedTranslationFields.title}
                  onChange={(e) => updateSelectedTranslationFields({ title: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Landing Page Body</label>
                <textarea
                  className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
                  rows={10}
                  value={selectedTranslationFields.landingPage}
                  onChange={(e) => updateSelectedTranslationFields({ landingPage: e.target.value })}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm mb-1">Voucher Headline</label>
                  <input
                    type="text"
                    className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
                    value={selectedTranslationFields.voucherOffer.headline}
                    onChange={(e) => updateSelectedTranslationFields({ voucherOffer: { headline: e.target.value } })}
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Voucher Button Label</label>
                  <input
                    type="text"
                    className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
                    value={selectedTranslationFields.voucherOffer.submitLabel}
                    onChange={(e) => updateSelectedTranslationFields({ voucherOffer: { submitLabel: e.target.value } })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm mb-1">Voucher Body</label>
                  <textarea
                    className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
                    rows={3}
                    value={selectedTranslationFields.voucherOffer.body}
                    onChange={(e) => updateSelectedTranslationFields({ voucherOffer: { body: e.target.value } })}
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Voucher Offer Title</label>
                  <input
                    type="text"
                    className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
                    value={selectedTranslationFields.voucherOffer.offerTitle}
                    onChange={(e) => updateSelectedTranslationFields({ voucherOffer: { offerTitle: e.target.value } })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm mb-1">Voucher Terms</label>
                  <textarea
                    className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
                    rows={3}
                    value={selectedTranslationFields.voucherOffer.terms}
                    onChange={(e) => updateSelectedTranslationFields({ voucherOffer: { terms: e.target.value } })}
                  />
                </div>
              </div>
              <div className="rounded border border-gray-800 bg-gray-950/60 p-3 text-xs text-gray-400">
                Save the campaign to persist these edits and refresh freshness metadata. `Translate Now` saves first, then replaces this language with a fresh machine translation draft.
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button className="px-3 py-2 bg-gray-700 rounded text-white" onClick={closePanel}>
          Close
        </button>
        <button className="px-3 py-2 bg-indigo-600 rounded text-white" onClick={() => handleSavePanel('translations')}>
          Save
        </button>
      </div>
    </div>
  );

  const renderAdvertisingEditor = () => (
    <div className="space-y-4">
      <div className="rounded border border-gray-800 bg-gray-950/60 p-3 text-sm text-gray-300">
        Choose where this campaign should appear as advertising across your landing pages. For catalog pages you can add it as a new ad section, join an existing rotating ad, or turn a single ad into a rotating slot. Campaign landing pages use the `Other Promotions` block directly under the text.
      </div>
      {advertisingPlacementCompositionChanged && nonSourceCampaignLanguageVersions.length > 0 && (
        <div className="rounded border border-amber-700/60 bg-amber-950/30 px-3 py-3 text-sm text-amber-100">
          <div className="font-semibold text-amber-50">Translation review needed on parent surfaces</div>
          <div className="mt-1">
            This campaign’s advertising placements changed while translated campaign content already exists.
          </div>
          <div className="mt-1 text-xs text-amber-100/80">
            Save first, then review any translated landing pages or catalog pages that now surface this campaign so their parent-language copy still reads correctly.
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-3">
        <input
          className="flex-1 rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
          placeholder="Search sites, pages, sections"
          value={advertisingFilter}
          onChange={(e) => setAdvertisingFilter(e.target.value)}
        />
        <button
          className="px-3 py-2 rounded bg-gray-800 text-white text-sm disabled:bg-gray-900 disabled:text-gray-500"
          onClick={loadAdvertisingTargets}
          disabled={advertisingTargetsLoading}
        >
          {advertisingTargetsLoading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {advertisingTargetsError && (
        <div className="text-sm text-red-300">{advertisingTargetsError}</div>
      )}

      <div className="rounded border border-gray-800 bg-black/20 p-3">
        <div className="text-sm font-semibold text-white">Selected Placements</div>
        <div className="mt-1 text-xs text-gray-400">
          {selectedAdvertisingPlacements.length
            ? `${selectedAdvertisingPlacements.length} landing-page placement${selectedAdvertisingPlacements.length === 1 ? '' : 's'} will include this campaign after save.`
            : 'No placements selected yet.'}
        </div>
        {!!selectedAdvertisingPlacements.length && (
          <div className="mt-3 flex flex-wrap gap-2">
            {selectedAdvertisingPlacements.map((placement) => (
              <button
                key={`placement-pill-${placement.selectionKey}`}
                type="button"
                className="rounded-full border border-indigo-700 bg-indigo-950/40 px-3 py-1 text-xs text-indigo-100"
                onClick={() => toggleAdvertisingSelection(placement.selectionKey)}
              >
                {placement.targetTitle}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-5 max-h-[55vh] overflow-y-auto pr-1">
        {filteredAdvertisingGroups.map((group) => (
          <div key={group.key} className="space-y-3">
            <div className="text-sm font-semibold uppercase tracking-wide text-gray-300">{group.label}</div>
            {!group.sites?.length && (
              <div className="rounded border border-gray-800 bg-gray-950/60 px-3 py-3 text-sm text-gray-400">
                No landing pages found in this group.
              </div>
            )}
            {(group.sites || []).map((siteGroup) => (
              <div key={`site-${group.key}-${siteGroup.siteId}`} className="rounded border border-gray-800 bg-gray-950/50 p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-white">{siteGroup.siteName || 'Unnamed site'}</div>
                    <div className="text-xs text-gray-500">
                      {(siteGroup.targets || []).length} landing page{(siteGroup.targets || []).length === 1 ? '' : 's'} available
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {(siteGroup.targets || []).map((target) => (
                    <div key={target.targetKey} className="rounded border border-gray-800 bg-black/20 p-3 space-y-3">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-full border border-gray-700 bg-gray-900 px-2 py-0.5 text-[11px] uppercase tracking-wide text-gray-300">
                              {target.targetType === 'menu'
                                ? 'Menu'
                                : target.targetType === 'products'
                                  ? 'Product QR'
                                  : target.targetType === 'services'
                                    ? 'Service QR'
                                    : 'Campaign'}
                            </span>
                            <div className="text-sm font-semibold text-white">{target.title}</div>
                          </div>
                          <div className="mt-1 text-xs text-gray-400">
                            {target.subtitle || 'Landing page'}
                            {target.landingUrl ? ` • ${target.landingUrl}` : ''}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {(target.placements || []).length} option{(target.placements || []).length === 1 ? '' : 's'}
                        </div>
                      </div>

                      <div className="space-y-2">
                        {(target.placements || []).map((placement) => {
                          const isSelected = advertisingSelectionKeys.includes(String(placement.selectionKey));
                          return (
                            <button
                              key={placement.selectionKey}
                              type="button"
                              className={`w-full rounded border px-3 py-3 text-left transition ${
                                isSelected
                                  ? 'border-indigo-500 bg-indigo-950/30'
                                  : 'border-gray-800 bg-gray-950/70 hover:border-gray-700 hover:bg-gray-900'
                              }`}
                              onClick={() => toggleAdvertisingSelection(placement.selectionKey)}
                            >
                              <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-white">{placement.label}</div>
                                  <div className="mt-1 text-xs text-gray-400">{placement.summary}</div>
                                  {!!placement.existingCampaigns?.length && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {placement.existingCampaigns.slice(0, 5).map((campaignPlacement) => (
                                        <div
                                          key={`${placement.selectionKey}-${campaignPlacement.campaignId}`}
                                          className="rounded-full border border-gray-700 bg-black/30 px-2 py-1 text-[11px] text-gray-300"
                                        >
                                          {campaignPlacement.title}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className={`text-xs font-semibold ${isSelected ? 'text-indigo-200' : 'text-gray-500'}`}>
                                  {isSelected ? 'Will include this campaign' : 'Click to include'}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <button className="px-3 py-2 bg-gray-700 rounded text-white" onClick={closePanel}>
          Close
        </button>
        <button className="px-3 py-2 bg-indigo-600 rounded text-white" onClick={() => handleSavePanel('advertising')}>
          Save
        </button>
      </div>
    </div>
  );

  const renderActivePanel = () => {
    if (activePanel === 'image') return renderImageEditor();
    if (activePanel === 'tv') return renderTvEditor();
    if (activePanel === 'social') return renderSocialEditor();
    if (activePanel === 'announcement') return renderAnnouncementEditor();
    if (activePanel === 'overlay') return renderOverlayEditor();
    if (activePanel === 'landing') return renderLandingEditor();
    if (activePanel === 'advertising') return renderAdvertisingEditor();
    if (activePanel === 'translations') return renderTranslationsEditor();
    return null;
  };

  return (
    <div className="mt-4 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="w-full md:flex-1">
          <label className="block text-lg font-semibold text-white mb-1">Campaign Title</label>
          <input
            type="text"
            className="p-2 bg-gray-800 text-white rounded w-full"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <div className="mt-3 flex flex-wrap items-center gap-3 rounded border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-300">
            <span className="whitespace-nowrap">Monetisation Mode</span>
            <select
              className="rounded bg-gray-800 px-2 py-1 text-white border border-gray-700 disabled:bg-gray-900 disabled:text-gray-500"
              value={effectiveMonetisationMode}
              onChange={(e) => setMonetisationMode(e.target.value)}
              disabled={monetisationIsEnforced}
            >
              {MONETISATION_MODE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="text-xs text-gray-500">
              {monetisationIsEnforced
                ? 'Free-tier sites always use Full monetisation.'
                : `${MONETISATION_MODE_OPTIONS.find((option) => option.value === effectiveMonetisationMode)?.description || 'No automatic landing page monetisation.'} Site default: ${MONETISATION_MODE_OPTIONS.find((option) => option.value === siteDefaultMonetisationMode)?.label || 'Off'}.`}
            </span>
          </div>
          <div className="mt-3 rounded border border-cyan-900/60 bg-cyan-950/20 px-3 py-3 text-sm text-cyan-50 space-y-1">
            <div>
              <span className="text-cyan-100/70">This campaign belongs to:</span>{' '}
              <span className="font-semibold text-white">{hostCampaignLabel || 'Current site'}</span>
            </div>
            {promotedVenueLabels.length > 0 ? (
              <div>
                <span className="text-cyan-100/70">This campaign promotes:</span>{' '}
                <span className="text-white">{promotedVenueLabels.join(', ')}</span>
              </div>
            ) : (
              <div className="text-cyan-100/70">This campaign promotes the host site itself.</div>
            )}
            {sourceCatalogSummary && (
              <div>
                <span className="text-cyan-100/70">Shared products source:</span>{' '}
                <span className="text-white">{sourceCatalogSummary}</span>
              </div>
            )}
            {promotionContext.mode === 'multi_site_promotion' && (
              <div className="text-xs text-cyan-100/70">
                Copy should speak as the host venue and encourage a later or different visit to the promoted venue.
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            className="px-3 py-1 bg-indigo-600 rounded text-white"
            onClick={() => handleSaveAll({ silentAnnouncement: true, persistAnnouncement: false })}
          >
            Save All
          </button>
          <button className="px-3 py-1 bg-red-600 rounded text-white" onClick={handleDelete}>
            Delete
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-800 bg-gray-950/80 p-4">
        {activePanel !== 'image' && (
          <div className="hidden" aria-hidden="true">
            <PhotoAssetBrowser
              ref={photoBrowserRef}
              assets={assets}
              siteId={site?.id}
              promptText={promptText}
              campaignTitle={title}
              selectedAssetId={selectedAssetId}
              onSelectAsset={handleAssetSelect}
              onUploadComplete={onRefreshAssets}
            />
          </div>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Campaign Panels</h3>
            <p className="text-sm text-gray-400">
              {panelsLocked
                ? 'Artwork is still generating. Panels will unlock automatically when it finishes.'
                : 'Open each panel to edit that part of the campaign without expanding the whole workspace.'}
            </p>
          </div>
          <div className="text-xs uppercase tracking-wide text-gray-500">
            {panelsLocked
              ? 'Locked during artwork generation'
              : activePanel
                ? `Editing ${PANEL_TITLES[activePanel]}`
                : 'Select a panel to edit'}
          </div>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {panelDescriptors.map((panel) => (
            <button
              key={panel.key}
              type="button"
              className={`rounded-xl border p-4 text-left transition ${
                activePanel === panel.key
                  ? 'border-indigo-500 bg-indigo-950/30'
                  : 'border-gray-800 bg-gray-900/80 hover:border-gray-700 hover:bg-gray-900'
              } ${panelsLocked ? 'cursor-not-allowed opacity-80' : ''}`}
              onClick={() => openPanel(panel.key)}
              disabled={panelsLocked}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-xl border text-xl ${
                      panelStatusLookup[panel.key]?.state === 'complete'
                        ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-200'
                        : panelStatusLookup[panel.key]?.state === 'in_progress'
                          ? 'border-indigo-500/50 bg-indigo-500/15 text-sm font-semibold uppercase tracking-wide text-indigo-100'
                          : panelStatusLookup[panel.key]?.state === 'error'
                            ? 'border-red-500/50 bg-red-500/15 text-red-200'
                            : 'border-gray-700 bg-gray-800 text-gray-500'
                    }`}
                  >
                    {panelStatusLookup[panel.key]?.state === 'complete'
                      ? '✓'
                      : panelStatusLookup[panel.key]?.state === 'in_progress'
                        ? '...'
                        : panelStatusLookup[panel.key]?.state === 'error'
                          ? '!'
                          : '□'}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{panel.title}</div>
                    <div className="mt-1 text-xs uppercase tracking-wide text-gray-500">{panel.accent}</div>
                  </div>
                </div>
                <span
                  className={`rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-wide ${
                    panelCompletion[panel.key]
                      ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-500/30'
                      : 'bg-gray-800 text-gray-300 border border-gray-700'
                  }`}
                >
                  {panelCompletion[panel.key] ? 'Saved' : panelsLocked ? 'Locked' : 'Open'}
                </span>
              </div>
              <div className="mt-4 text-sm text-gray-200">{panel.summary}</div>
              <div className="mt-2 text-xs text-gray-500">{panelStatusLookup[panel.key]?.label || panel.detail}</div>
              <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-indigo-300">
                {panelsLocked ? 'Waiting for artwork' : 'Edit panel'}
              </div>
            </button>
          ))}
        </div>
      </div>

      {activePanel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={closePanel}
        >
          <div
            className="relative w-full max-w-6xl max-h-[92vh] overflow-y-auto rounded-2xl border border-gray-800 bg-gray-900 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="absolute right-4 top-4 text-2xl leading-none text-gray-400 hover:text-white"
              onClick={closePanel}
              aria-label="Close editor"
            >
              ×
            </button>
            <div className="mb-6 pr-10">
              <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Campaign Editor</div>
              <h3 className="mt-2 text-2xl font-semibold text-white">{PANEL_TITLES[activePanel]}</h3>
              <p className="mt-1 text-sm text-gray-400">
                {title || 'Untitled campaign'}
              </p>
            </div>
            {renderActivePanel()}
          </div>
        </div>
      )}
    </div>
  );
}
